import {
  type Collection,
  List as ImmutableList,
  Map as ImmutableMap,
  Set as ImmutableSet,
  isImmutable,
  merge,
  OrderedMap,
  Record,
  type RecordOf,
} from 'immutable';
import { DateTime } from 'luxon';
import { createCachedSelector, LruObjectCache } from 're-reselect';
import { createSelector as createSelectorBase } from 'reselect';

import type { ChartOfAccountNode } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import {
  getNormalizedFilteredTxns,
  getNormalizedSortedGoals,
  getNormalizedSortedTxns,
} from 'app-components/flux/free-to-spend/freeToSpendUtils';
import { tracker } from 'app-components/utils/core';
import {
  createWeakMapSelector,
  immutableEntriesShallowEqual,
  resettableLruMemoize,
} from 'app-components/utils/core/reselect';
import { registerCachedSelectors } from 'app-components/utils/core/reselect/selectorCacheRegistry';
import { EPOCH_ISO } from 'app-components/utils/date';
import { numberUtils } from 'app-components/utils/number';
import type RootState from 'app-components/utils/redux-store/rootState';

// other stores
import { type ACCOUNTS_REDUCER_KEY, accountsSelectors } from '../accounts';
import { type CATEGORIES_REDUCER_KEY, categoriesSelectors, categoriesTypes } from '../categories';
import { chartOfAccountsTypes, chartOfAccountsUtils } from '../chart-of-accounts';
import { selectorsHelpers } from '../core';
import { type DATE_REDUCER_KEY, dateSelectors } from '../date';
import { type FEATURE_FLAGS_REDUCER_KEY, featureFlagsSelectors } from '../feature-flags';
import { type FILTERS_REDUCER_KEY, filtersSelectors, filtersTypes, filtersUtils } from '../filters';
import { type GOALS_REDUCER_KEY, goalsSelectors, type goalsTypes, goalsUtils } from '../goals';
import { type PREFERENCES_V2_REDUCER_KEY, preferencesV2Selectors } from '../preferences-v2';
import { getResourcesById as getScheduledTxnsById } from '../scheduled-transactions/scheduledTransactionsSelectors';
import type {
  SCHEDULED_TRANSACTIONS_REDUCER_KEY,
  ScheduledTransaction,
} from '../scheduled-transactions/scheduledTransactionsTypes';
import {
  getMatchedSplitReminderSeriesBucket,
  getScheduledModelTransaction,
  getSeriesPrimaryCoa,
  isIncomeSeriesFromTxn,
} from '../scheduled-transactions/scheduledTransactionsUtils';
import type { SECURITIES_REDUCER_KEY } from '../securities/securitiesTypes';
import { transactionsSelectors, transactionsUtils } from '../transactions';
import {
  getOrderedSplitItemsForF2S,
  getTransferSplitItemForF2S,
  isOtherSideOfTransferTxnASplit,
  isOverflowSplitItem,
  isZeroAmountOverflowSplit,
  splitItemsToArray,
  splitItemsToArrayOverflowLast,
} from '../transactions/splitsHelpers';
import {
  mkCashFlowTransaction,
  mkSplitItem,
  mkTransactionItem,
  type SplitItemProps,
  type TRANSACTIONS_REDUCER_KEY,
  type TransactionItem,
  type TransactionItemProps,
  type TransactionProps,
  type TransactionRecord,
} from '../transactions/transactionsTypes';
// self
import {
  type BillsFilterType,
  FREE_TO_SPEND_REDUCER_KEY,
  type FreeToSpend,
  mkFreeToSpend,
  type PlannedSpendingConflict,
  type PlannedSpendingItem,
  type PlannedSpendingItems,
  type SpendingPlanBreakDownForPageDisplay,
  validRolloverStates,
} from './freeToSpendTypes';

const emptyPlannedList = ImmutableList<PlannedSpendingItem>();
const emptyTxnList = ImmutableList<TransactionRecord>();
const emptyTxnMap = ImmutableMap<string, TransactionRecord>();
const emptyItemsList = ImmutableList<TransactionItem>();
const emptyStringSet = ImmutableSet<string>();
const emptyScheduledTransactions = ImmutableMap<string, ScheduledTransaction>();
const emptyFilters = ImmutableMap<string, filtersTypes.FilterProps>();
const emptyGoalsList = ImmutableList<goalsTypes.Goal>();

type TxnList = ImmutableList<TransactionRecord>;
type TxnMap = ImmutableMap<string, TransactionRecord>;
type TxnsCollection = Collection<string | number, TransactionProps>;
type State = Pick<
  RootState,
  | typeof FREE_TO_SPEND_REDUCER_KEY
  | typeof TRANSACTIONS_REDUCER_KEY
  | typeof ACCOUNTS_REDUCER_KEY
  | typeof CATEGORIES_REDUCER_KEY
  | typeof FEATURE_FLAGS_REDUCER_KEY
  | typeof GOALS_REDUCER_KEY
  | typeof SCHEDULED_TRANSACTIONS_REDUCER_KEY
  | typeof SECURITIES_REDUCER_KEY
  | typeof DATE_REDUCER_KEY
  | typeof FILTERS_REDUCER_KEY
  | typeof PREFERENCES_V2_REDUCER_KEY
>;

// ======UTILS========================================

const roundOff = (num: number, places: number) => {
  const x = Math.pow(10, places);
  return Math.round(num * x) / x;
};

function isNumber(value: any) {
  return typeof value === 'number';
}

const getItemsSum = (txnItems?: TransactionItemProps[]) =>
  roundOff(txnItems?.reduce((acc, item) => acc + (item?.item?.amount || 0), 0) || 0, 2);

const getIncludedItemsSum = (txnItems?: TransactionItemProps[]) =>
  roundOff(
    txnItems?.reduce(
      (acc, item) => acc + (item?.item?.amount && !item?.item?.isExcludedFromF2S ? item?.item?.amount : 0),
      0,
    ) || 0,
    2,
  );

// Business split lines should be visible in tooltips but ignored in Spending Plan math.
const isBusinessSplitItem = (splitItem: SplitItemProps | null | undefined): boolean => {
  const businessId = splitItem?.businessId ?? null;
  return Boolean(businessId && businessId !== '0');
};

const getReminderPrimarySplitItem = (splitItems: SplitItemProps[]): SplitItemProps | null => {
  // only allow personal reminders to be considered for spending plan
  const personalSplitItems = splitItems.filter((splitItem) => splitItem && !isBusinessSplitItem(splitItem));

  const firstNonTransfer = personalSplitItems.find(
    (splitItem) => splitItem && !transactionsUtils.isTransferOrCreditCardPaymentTxn(splitItem),
  );
  if (firstNonTransfer) return firstNonTransfer;

  return personalSplitItems.find((splitItem) => splitItem) ?? null;
};

const getReminderParentCoa = (splitItems: SplitItemProps[]): SplitItemProps['coa'] | null =>
  getReminderPrimarySplitItem(splitItems)?.coa ?? null;

const getIncomeReminderParentAmount = ({
  splitItems,
  plannedCoaIds,
  fallbackAmount,
}: {
  splitItems: SplitItemProps[];
  plannedCoaIds: ImmutableSet<string>;
  fallbackAmount: number;
}): number => {
  const primarySplitItem = getReminderPrimarySplitItem(splitItems);
  const hasPlannedSplitLine = splitItems.some((splitItem) => plannedCoaIds.has(splitItem.coa?.id ?? ''));

  if (
    hasPlannedSplitLine &&
    primarySplitItem &&
    !transactionsUtils.isTransferOrCreditCardPaymentTxn(primarySplitItem)
  ) {
    return primarySplitItem.amount;
  }

  return fallbackAmount;
};

const getReminderSeriesBucket = ({
  txn,
  scheduledTxnsById,
  incomeCOAIds,
}: {
  txn: TransactionProps | null | undefined;
  scheduledTxnsById: Parameters<typeof getScheduledModelTransaction>[1];
  incomeCOAIds: Parameters<typeof isIncomeSeriesFromTxn>[1];
}): ReturnType<typeof getMatchedSplitReminderSeriesBucket> => {
  const directBucket = getMatchedSplitReminderSeriesBucket({
    txn,
    scheduledTxnsById,
    incomeCOAIds,
  });
  if (directBucket !== null) return directBucket;
  const isAcceptedReminder = txn?.source === 'SCHEDULED_TRANSACTION';
  const isPendingReminder = txn?.source === 'SCHEDULED_TRANSACTION_PENDING';
  if (!isAcceptedReminder && !isPendingReminder) return null;
  if (!transactionsUtils.isRealizedReminder(txn)) return null;

  const seriesTxn = getScheduledModelTransaction(txn, scheduledTxnsById);
  if (!seriesTxn || !transactionsUtils.isSplitTxn(seriesTxn)) return null;
  if (isIncomeSeriesFromTxn(seriesTxn, incomeCOAIds)) return null;

  return seriesTxn.isSubscription || txn.isSubscription ? 'subscription' : 'bill';
};

const isTransferSeriesTxn = ({
  txn,
  scheduledTxnsById,
}: {
  txn: TransactionProps | null | undefined;
  scheduledTxnsById: Parameters<typeof getScheduledModelTransaction>[1];
}): boolean => {
  const seriesTxn = getScheduledModelTransaction(txn, scheduledTxnsById) ?? txn;
  const primaryCoa = getSeriesPrimaryCoa(seriesTxn);
  return (
    primaryCoa?.type === chartOfAccountsTypes.CoaTypeEnum.ACCOUNT ||
    chartOfAccountsUtils.isTransferLikeBalanceAdjustmentCoa(primaryCoa)
  );
};

const shouldEmitReminderParentRow = ({
  txn,
  splitItems,
  scheduledTxnsById,
}: {
  txn: TransactionProps;
  splitItems: SplitItemProps[];
  scheduledTxnsById: Parameters<typeof getScheduledModelTransaction>[1];
}): boolean => {
  if (!isTransferSeriesTxn({ txn, scheduledTxnsById })) return true;

  return splitItems.some(
    (splitItem) =>
      splitItem &&
      !isOverflowSplitItem(splitItem) &&
      !isBusinessSplitItem(splitItem) &&
      !transactionsUtils.isTransferOrCreditCardPaymentTxn(splitItem),
  );
};

const getReminderSplitLineIsExcludedFromF2S = ({
  txn,
  splitItem,
  rawSplitItem,
  modelSplitItem,
  scheduledTxnsById,
  otherSideForSplit,
}: {
  txn: TransactionProps;
  splitItem: SplitItemProps;
  rawSplitItem?: SplitItemProps;
  modelSplitItem?: SplitItemProps;
  scheduledTxnsById: Parameters<typeof getScheduledModelTransaction>[1];
  otherSideForSplit?: TransactionRecord;
}): boolean => {
  const transferSeries = isTransferSeriesTxn({ txn, scheduledTxnsById });
  // Precedence: instance flag (raw, then post-transform) wins, then the series model flag.
  // The model fallback catches series-level exclusions that the instance row replaced without copying.
  const explicitSplitExclusion =
    rawSplitItem?.isExcludedFromF2S ?? splitItem.isExcludedFromF2S ?? modelSplitItem?.isExcludedFromF2S;
  if (
    explicitSplitExclusion != null &&
    !transactionsUtils.isTransferOrCreditCardPaymentTxn(rawSplitItem ?? splitItem)
  ) {
    return explicitSplitExclusion;
  }

  if (rawSplitItem?.isExcludedFromF2S != null) {
    return rawSplitItem.isExcludedFromF2S;
  }

  if (transactionsUtils.isTransferOrCreditCardPaymentTxn(splitItem)) {
    return transferSeries
      ? (otherSideForSplit?.isExcludedFromF2S ?? txn.isExcludedFromF2S ?? true)
      : (txn.isExcludedFromF2S ?? false);
  }

  if (transferSeries) {
    return false;
  }

  return txn.isExcludedFromF2S ?? false;
};

const getReminderIncludedNonTransferAmount = ({
  txn,
  splitItems,
  rawSplitItems,
  scheduledTxnsById,
}: {
  txn: TransactionProps;
  splitItems: SplitItemProps[];
  rawSplitItems: SplitItemProps[];
  scheduledTxnsById: Parameters<typeof getScheduledModelTransaction>[1];
}): number | undefined => {
  let hasTransferSplit = false;
  let hasExcludedNonTransferSplit = false;

  const includedAmount = splitItems.reduce((sum, splitItem, index) => {
    if (isOverflowSplitItem(splitItem)) return sum;

    if (transactionsUtils.isTransferOrCreditCardPaymentTxn(splitItem)) {
      hasTransferSplit = true;
      return sum;
    }

    const rawSplitItem = rawSplitItems[index];
    const isExcludedFromF2S = getReminderSplitLineIsExcludedFromF2S({
      txn,
      splitItem,
      rawSplitItem,
      scheduledTxnsById,
    });
    if (isExcludedFromF2S) {
      hasExcludedNonTransferSplit = true;
      return sum;
    }

    return sum + Number(splitItem.amount ?? 0);
  }, 0);

  if (!hasTransferSplit && !hasExcludedNonTransferSplit) return undefined;

  return includedAmount;
};

// excludeReports and excludeF2S track split-line exclusions!
const getTxnSumForCoa = (txns?: TxnsCollection, coaIds?: ImmutableSet<string>, negator = true): number =>
  txns?.reduce((acc: number, txn: TransactionProps) => {
    let amount = txn.amount || 0;
    if (txn?.split?.items && isImmutable(txn.split.items) && txn?.split?.items.size > 0 && coaIds) {
      amount = txn.split.items.reduce(
        (accumulator: number, item: SplitItemProps) =>
          negator === coaIds.has(item.coa?.id ?? '') && !item.isExcludedFromF2S
            ? accumulator + item.amount
            : accumulator,
        0,
      );
    }
    return roundOff(acc + amount, 2);
  }, 0) || 0;

const getTxnSumWithCallback = (txns: TxnList, customCallback: any, negator = true) =>
  txns?.reduce((acc, txn) => {
    let amount = txn.amount || 0;
    if (txn?.split?.items && isImmutable(txn.split.items) && txn?.split?.items.size > 0) {
      amount = txn.split.items.reduce(
        (accumulator, item) => (negator === customCallback(item) ? accumulator + item.amount : accumulator),
        0,
      );
    }
    return roundOff(acc + amount, 2);
  }, 0) || 0;

const transferAndCoaAndBizCallback = (item: TransactionProps, coaIds: ImmutableSet<string>) =>
  item.isExcludedFromF2S ||
  item.businessId ||
  transactionsUtils.isTransferOrCreditCardPaymentTxn(item) ||
  (item.coa?.id && coaIds.includes(item.coa.id));
const getValueFromFilterAndTransactions = ({
  filters,
  transactions,
  filterId,
  date,
  scheduledTxnsById,
  incomeCOAIds,
}: {
  filters: any;
  transactions: any;
  filterId: string;
  date: string;
  scheduledTxnsById: ReturnType<typeof getScheduledTxnsById>;
  incomeCOAIds: ReturnType<typeof categoriesSelectors.getIncomeCOAIds>;
}) => {
  const filterObject = filtersUtils.convertQCSFilterToFilterObject(filters.get(filterId));
  const txns = transactionsUtils.filterTransactionsByFilterObject(
    transactions,
    filterObject.merge({
      dateStart: date,
      dateEnd: DateTime.fromISO(date).endOf('month').toISODate(),
      advanced: filtersTypes.AdvancedFilterObject({ isBillOrSubscription: false, isExcludedFromF2S: false }),
    }),
  );
  const coaIdMap = filters.get(filterId)?.filterItems?.[0]?.chartOfAccounts?.map((coa: ChartOfAccountNode) => coa?.id);
  const filteredTxns = Array.isArray(txns)
    ? txns
    : isImmutable(txns) && typeof txns.valueSeq === 'function'
      ? txns.valueSeq().toArray()
      : [];
  const visiblePlannedTxns = filteredTxns.filter((txn: TransactionProps) =>
    shouldIncludeTxnInPlannedSpending({ txn, scheduledTxnsById, incomeCOAIds }),
  );
  return getTxnSumForCoa(ImmutableList(visiblePlannedTxns), ImmutableSet(coaIdMap));
};

// ======UTILS=========END===============================

const shouldUseSeriesModelSplitItemsForTxn = (txn: Pick<TransactionProps, 'source'> | null | undefined): boolean =>
  txn?.source === 'SCHEDULED_TRANSACTION_PENDING';

const shouldIncludeTxnInPlannedSpending = ({
  txn,
  scheduledTxnsById,
  incomeCOAIds,
}: {
  txn: TransactionProps;
  scheduledTxnsById: ReturnType<typeof getScheduledTxnsById>;
  incomeCOAIds: ReturnType<typeof categoriesSelectors.getIncomeCOAIds>;
}): boolean => {
  if (txn.source !== 'SCHEDULED_TRANSACTION' && txn.source !== 'SCHEDULED_TRANSACTION_PENDING') {
    return true;
  }

  if (!transactionsUtils.isRealizedReminder(txn)) {
    return false;
  }

  return (
    getReminderSeriesBucket({
      txn,
      scheduledTxnsById,
      incomeCOAIds,
    }) === null
  );
};

const createSelector = createSelectorBase.withTypes<State>();

const getStore = (state: State) => state[FREE_TO_SPEND_REDUCER_KEY];

export const getLoadPending = (state: State) => getStore(state).loadPending;
export const getIsLoading = (state: State) => getStore(state).isLoading;
export const getError = (state: State) => getStore(state).error;
export const getResourcesById = (state: State) => getStore(state).resourcesById;

export const getLastSyncDate = (state: State) => getStore(state).lastSyncDate;
export const getFreeToSpendById = (state: State, id: string) => getStore(state).resourcesById.get(id);
export const getFreeToSpendForDate = createSelector(
  [getResourcesById, (_state, date: string) => date],
  (resources, date) => resources.find((F2S) => F2S.date === date) || mkFreeToSpend(),
);

const filterMonthCandidates = selectorsHelpers.createIncrementalFilter(
  (transactions: ImmutableMap<string, TransactionRecord>, test: (txn: TransactionRecord) => unknown) =>
    transactions.filter(test),
);

const getTransactionCandidatesForMonth = createCachedSelector(
  [
    (_state: State, date: string) => date,
    transactionsSelectors.getResourcesById,
    accountsSelectors.getGoalAccountIds,
    dateSelectors.getCurrentDateString,
    accountsSelectors.getConnectedLoanAccountIds,
    (state: State) => featureFlagsSelectors.getFeatureFlag(state, 'hideConnectedLoanTransactions'),
  ],
  (
    date,
    transactions: ImmutableMap<string, TransactionRecord>,
    goalAccountIds,
    todayISO,
    connectedLoanAccountIds: ImmutableSet<string>,
    hideConnectedLoanTxnFF,
  ): TxnMap => {
    if (!date || !transactions) {
      return emptyTxnMap;
    }
    const ftsMonthYear = date.slice(0, -3);
    // Filter down the list to what we need. Edits re-test only the transactions they changed.
    return filterMonthCandidates(
      transactions,
      [goalAccountIds, todayISO, connectedLoanAccountIds, hideConnectedLoanTxnFF],
      (txn) =>
        txn?.postedOn?.startsWith(ftsMonthYear) &&
        txn.accountId &&
        (!hideConnectedLoanTxnFF || !connectedLoanAccountIds?.has(txn.accountId)) &&
        // Classify only this month's transactions, rather than filtering the entire history first.
        transactionsSelectors.isTransactionOfTypes(
          txn,
          goalAccountIds,
          transactionsSelectors.txnTypesCashAndPlanned,
          todayISO,
        ),
      date,
    );
  },
  { memoizeOptions: { resultEqualityCheck: immutableEntriesShallowEqual<string, TransactionRecord> } },
)({
  // Each active date retains its own last equal result when month consumers interleave.
  keySelector: (_state, date) => date || '',
  cacheObject: new LruObjectCache({ cacheSize: 12 }),
});

// Amount calculations may reuse rows after a parent memo edit. Keep this private:
// transaction-bearing detail selectors must continue to read the fresh candidate map.
const equalSummaryTransactions = (previous: TxnMap, next: TxnMap): boolean => {
  if (previous === next) return true;
  if (previous.size !== next.size) return false;
  const entries = next.entries();
  for (const [key, transaction] of previous) {
    const entry = entries.next();
    if (entry.done || entry.value[0] !== key) return false;
    const nextTransaction = entry.value[1];
    if (transaction !== nextTransaction && !nextTransaction.set('memo', transaction.memo).equals(transaction)) {
      return false;
    }
  }
  return true;
};

const getSummaryTransactionCandidatesForMonth = createCachedSelector(
  [getTransactionCandidatesForMonth],
  (transactions): TxnMap => transactions,
  {
    memoizeOptions: { resultEqualityCheck: equalSummaryTransactions },
    // This layer intentionally retains semantically equal inputs for amount readers.
    devModeChecks: { identityFunctionCheck: 'never' },
  },
)({ keySelector: (_state: State, date: string) => date || '', cacheObject: new LruObjectCache({ cacheSize: 12 }) });

const getTransactionsForMonthWithGoals = createSelector(
  [getTransactionCandidatesForMonth],
  (transactions): TxnMap => transactions.sort((a, b) => (a.postedOn > b.postedOn ? 1 : -1)),
);
// Both readers must share the transformed map before sharing financial calculations.
const getSummaryTransactionsForMonthWithGoals = createSelector(
  [getSummaryTransactionCandidatesForMonth],
  getTransactionsForMonthWithGoals.memoizedResultFunc,
);

type MonthTransactionsSelector = (state: State, date: string) => TxnMap;
// Detail and amount readers are built from the same input factories; only the transaction source differs.
// Deriving the amount reader from the detail reader's memoizedResultFunc shares one calculation cache, so
// identical inputs are computed once for either reader (and counted on the detail selector).
const monthTransactionInputs = (
  transactionsWithGoals: MonthTransactionsSelector,
): [
  MonthTransactionsSelector,
  typeof accountsSelectors.getGoalAccountIds,
  typeof categoriesSelectors.getBusinessCoaIdsSet,
] => [transactionsWithGoals, accountsSelectors.getGoalAccountIds, categoriesSelectors.getBusinessCoaIdsSet];

const getTransactionsForMonth = createSelector(
  monthTransactionInputs(getTransactionsForMonthWithGoals),
  (transactions, goalAccountIds, businessCoaIds): TxnMap =>
    transactions.filter(
      (txn) =>
        !txn.businessId && !businessCoaIds.has(txn.coa?.id ?? '') && !goalAccountIds.has(txn.accountId as string),
    ),
);
const getSummaryTransactionsForMonth = createSelector(
  monthTransactionInputs(getSummaryTransactionsForMonthWithGoals),
  getTransactionsForMonth.memoizedResultFunc,
);

// Motivation - want to get the transactions for a specific coa within a certain date range (usually 6 months) so that we can take the average
export const getTransactionsForCoa = createSelector(
  [
    (state) =>
      accountsSelectors.getLoadPending(state) ||
      categoriesSelectors.getLoadPending(state) ||
      transactionsSelectors.getLoadPending(state),
    transactionsSelectors.getTransactionsByAccountId,
    categoriesSelectors.getCategoriesById,
    (_, props) => props.coaId,
    (_, props) => props.pastCutoff,
    (_, props) => props.currCutoff,
  ],
  (isLoading, txnsByAccountId, categories, coaId, pastCutoff, currCutoff) => {
    if (isLoading || !coaId) {
      return null;
    }
    const cacheKey = selectorsHelpers.getOrCreateStringFromObject(
      'planned-spending-calculation',
      { txnsByAccountId, categories, coaId, pastCutoff, currCutoff },
      6,
      selectorsHelpers.memoizeWithKeyRetrieveCache,
    );

    return selectorsHelpers.memoizeWithKey(
      'planned-spending-calculation',
      () => {
        const retArr: TransactionRecord[] = [];
        let sum = 0;
        const localTime = DateTime.local();
        let firstTxnDate: DateTime = localTime;
        const pastDateTime = DateTime.fromSeconds(pastCutoff).startOf('month');
        const currDateTime = DateTime.fromSeconds(currCutoff).endOf('month');
        txnsByAccountId?.forEach((txns) => {
          txns?.forEach((txn) => {
            const txnDate: DateTime = transactionsUtils.dateTimeFromTxn(txn);
            const txnCat = categories.get(txn?.coa?.id ?? '');
            const searchedCat = categories.get(coaId);
            if (
              (txn?.coa?.id === coaId || txnCat?.parentId === searchedCat?.id) &&
              txnDate > pastDateTime &&
              txnDate < currDateTime &&
              !txn.stModelId &&
              txn.type !== 'INVESTMENT'
            ) {
              if (firstTxnDate > txnDate) {
                firstTxnDate = txnDate;
              }
              retArr.push(txn);
              sum += txn.amount;
            } else if (
              transactionsUtils.isSplitTxn(txn) &&
              txnDate > pastDateTime &&
              txnDate < currDateTime &&
              !txn.stModelId &&
              txn.type !== 'INVESTMENT'
            ) {
              if (firstTxnDate > txnDate) {
                firstTxnDate = txnDate;
              }
              txn?.split?.items?.forEach((x) => {
                if (x?.coa?.id === coaId) {
                  retArr.push(txn);
                  sum += x.amount;
                }
              });
            }
          });
        });
        let numMonths = 0;
        if (currDateTime > pastDateTime) {
          numMonths = Math.round(currDateTime.diff(pastDateTime, 'months').months);
        } else {
          numMonths = 6;
        }
        return { txns: retArr, numMonths, sum };
      },
      cacheKey,
      6,
    );
  },
);

export const getFilteredBillsAmount = createSelector(
  [
    (state, freeToSpend: FreeToSpend) => getSpendingPlanBreakdownDataWithSplitsV2(state, freeToSpend),
    (_state, _freeToSpend, billsFilter: BillsFilterType) => billsFilter,
  ],
  (spendingPlanBreakdown, billsFilter) => {
    const billsIncludedData = spendingPlanBreakdown.bills.includeData;

    const filteredTxns = getNormalizedFilteredTxns(billsIncludedData, billsFilter);
    let filterAmount = 0;
    if (filteredTxns) {
      filteredTxns.forEach((item) => {
        filterAmount = filterAmount + item.amount;
      });
    }
    return filterAmount;
  },
);

// ===============================================================================================================================================================================
// ==================================================================== PLANNED SPENDING SELECTORS START HERE ====================================================================
// ===============================================================================================================================================================================

export const getRolloverForItem = (
  items: (PlannedSpendingItem | undefined)[] | undefined,
  myStartDate: string,
  prevPlannedItem: PlannedSpendingItem | null | undefined,
  plannedItem: PlannedSpendingItem | null | undefined,
  futureFeatureFlag: boolean | undefined,
  currentDate: string,
  onlyCalculated?: boolean,
): number | null => {
  const startDate = myStartDate || (DateTime.fromISO(currentDate).startOf('month').toISODate() as string);
  const thisMonth = DateTime.fromISO(currentDate).startOf('month').toISODate() as string;
  const monthAfter = DateTime.fromISO(currentDate).startOf('month').plus({ months: 1 }).toISODate() as string;

  // If the rolloverType for the selected item is NONE, we do NOT show rollovers at ALL.
  if (plannedItem && !validRolloverStates.includes(plannedItem?.rolloverType)) {
    return 0;
  }

  // If the month is past the current month, we do NOT show rollovers unless the futureFeatureFlag is turned ON
  if (thisMonth < startDate && !futureFeatureFlag) {
    return 0;
  }

  // If the rolloverResetValue is set, we set rollovers to that amount
  if (typeof plannedItem?.resetRolloverAmount === 'number' && !onlyCalculated) {
    return plannedItem?.resetRolloverAmount as number;
  }

  // This is the cumulative rollover value of the plannedSpendingItem from the latest month before the selected month.
  let rolloverValue: number | null;
  if (prevPlannedItem && !validRolloverStates.includes(prevPlannedItem?.rolloverType)) {
    rolloverValue = onlyCalculated ? null : 0;
  } else {
    rolloverValue = prevPlannedItem?.cumulativeRolloverAmount || (onlyCalculated ? null : 0);
  }

  // QCS calculates the rolloverAmounts up to todaysDate, and todaysDates rolloverAmount is used in NEXT MONTHS plannedSpendingCard
  // Therefore, if the startDate is less than or EQUAL TO the month AFTER todaysDates' month, we will just use the available rolloverValue!
  // Weird huh?
  if (monthAfter >= startDate || !futureFeatureFlag) {
    return rolloverValue;
  } else {
    items?.forEach((item) => {
      if (typeof rolloverValue !== 'number') {
        rolloverValue = 0; // This is if the last item ISN'T null and we need to keep counting after!
      }
      if (item) {
        const target = item.overwrittenTargetAmount ?? (item.targetAmount || 0);
        const leftToSpend = target + (item.calculatedSpentAmount || 0);

        // We reset the rolloverAmount to nullify old values then add up the latest amounts
        if (typeof item?.resetRolloverAmount === 'number') {
          rolloverValue = item?.resetRolloverAmount as number;
          rolloverValue += leftToSpend;
        } else if (typeof item?.cumulativeRolloverAmount === 'number') {
          rolloverValue = item?.cumulativeRolloverAmount as number;
        } else if (
          // We cancelled the rollovers for this filterId at a later date,
          // but since the startDate item has a rollover, it means we re-enabled it later
          !validRolloverStates.includes(item?.rolloverType)
        ) {
          rolloverValue = 0;
        } else {
          // Finally, if all the checks pass, we sum up the value! Hooray!
          rolloverValue += leftToSpend;
        }
      } else {
        rolloverValue = onlyCalculated ? null : 0; // This is so we can end on null if the last item doesn't exist!
      }
    });
  }
  // We are intentionally returning null when there is no previous months' data.
  return rolloverValue;
};

// Checks if all planned spending items for a date are created on the QCS side.
// If we check this first, we can safely be certain we are only sending QCS real PSIs and not
// optimistically updated ones that do not exist to them yet.
export const doPlannedSpendingItemsForDateAllHaveIds = createSelector(
  [getResourcesById, (state, date: string | undefined) => date || dateSelectors.getStartOfMonthDate(state)],
  (intervals, date) => {
    const freeToSpend = intervals.find((interval) => interval.date === date);
    return freeToSpend?.plannedSpendingItems?.every((item) => !!item?.id) || false;
  },
);

const sortPlannedSpendingItems = (
  plannedSpendingItems: PlannedSpendingItems | undefined,
  sortByAmount?: boolean,
  filters?: ReturnType<typeof filtersSelectors.getFilters>,
  categoriesById?: ReturnType<typeof categoriesSelectors.getCategoriesById>,
) => {
  if (sortByAmount) {
    return (
      plannedSpendingItems?.sortBy(
        (item) => item?.overwrittenTargetAmount ?? item?.targetAmount,
        (a, b) => (b ?? 0) - (a ?? 0),
      ) || emptyPlannedList
    );
  } else {
    return (
      plannedSpendingItems?.sortBy(
        (item) => {
          const name = item?.name?.trim();
          if (name) return name;
          const filter = filters?.get(item?.filterId ?? '');
          const firstFilterItem = filter?.filterItems?.[0];
          if (firstFilterItem?.type === 'CHARTOFACCOUNT') {
            const coaId = firstFilterItem.chartOfAccounts?.[0]?.id;
            return categoriesById?.get(coaId ?? '')?.name?.trim() ?? '';
          }
          return '';
        },
        (a, b) => (a ?? '').localeCompare(b ?? ''),
      ) || emptyPlannedList
    );
  }
};

const getUnsortedPlannedSpendingItemsForDate = createSelector(
  [getResourcesById, (state, date: string | undefined) => date || dateSelectors.getStartOfMonthDate(state)],
  (intervals, date): PlannedSpendingItems =>
    intervals.find((interval) => interval.date === date)?.plannedSpendingItems ?? emptyPlannedList,
);

export const getPlannedSpendingItemsForDate = createSelector(
  [
    getUnsortedPlannedSpendingItemsForDate,
    (state) => preferencesV2Selectors.getPreferenceValue(state, 'sSortPlannedItemsByAmount'),
    filtersSelectors.getFilters,
    categoriesSelectors.getCategoriesById,
  ],
  (plannedItems, sortByAmount, filters, categoriesById) =>
    sortPlannedSpendingItems(plannedItems, sortByAmount, filters, categoriesById),
);

export const getPlannedItemByDateAndId = createSelector(
  [getPlannedSpendingItemsForDate, (_state, _startDate: string, plannedId: string) => plannedId],
  (plannedItems, id) => plannedItems.find((item) => item.id === id) || null,
);

export const getPlannedItemByDateAndFilterId = createSelector(
  [
    (state, startDate: string) => getPlannedSpendingItemsForDate(state, startDate),
    (_state, _startDate: string, filterId: string) => filterId,
  ],
  (plannedItems, id) => plannedItems.find((item) => item.filterId === id) || null,
);

// Get all intervals during and after the current date. We do not need to worry about any intervals before the current date because those are calculated
// by QCS and all we need to do in that case is call (getPreviousMonthsPlannedItemByDateAndFilterId)
export const getFutureFreeToSpendIntervalsIncludingToday = createSelector(
  getResourcesById,
  dateSelectors.getCurrentDateString,
  (intervals, currentDate) => {
    const date = DateTime.fromISO(currentDate).startOf('month').toISODate() as string;
    return intervals.filter((interval) => interval?.date && interval.date >= date).sortBy((interval) => interval.date);
  },
);

// Get either today's date or the interval closest to today's date (after today's date) This is how we determine which
// planned spending item will be affected by edits made to rollovers! (We only allow the user to edit today's dates item, and if it does
// not exist, then we default to the next available future item)
export const getEarliestPlannedSpendingItemNotInPastByFilterId = createSelector(
  [getFutureFreeToSpendIntervalsIncludingToday, (_state, filterId: string) => filterId],
  (intervals, filterId) => {
    let selectedItem: null | PlannedSpendingItem | undefined = null;
    let date: null | string = null;
    let f2sObject: null | FreeToSpend = null;

    // Search the months for the planned spending item with the same filterId.
    for (const f2sMonth of intervals.values()) {
      const newItem = f2sMonth?.plannedSpendingItems?.find((item) => item.filterId === filterId);
      if (newItem) {
        f2sObject = f2sMonth;
        date = f2sMonth.date;
        selectedItem = newItem;
        break; // Stop iteration once the first matching item is found.
      }
    }

    return {
      f2sObject,
      selectedItem,
      date,
    };
  },
);

// Search through the future intervals to grab the correct plannedSpending items by filterId. They should be after todaysDate, but BEFORE the STARTDATE.
export const getPlannedSpendingItemsBeyondDateByFilterId = createSelector(
  [
    getFutureFreeToSpendIntervalsIncludingToday,
    (_state, _startDate: string, filterId: string) => filterId,
    (_state, startDate: string) => startDate,
    dateSelectors.getCurrentDateString,
  ],
  (intervals, filterId, myStartDate, currentDate) => {
    const startDate = myStartDate || DateTime.fromISO(currentDate).startOf('month').toISODate();
    const plannedItemList: (PlannedSpendingItem | undefined)[] = [];

    const intervalsToSum = intervals
      .filter((month) => month?.date && startDate && startDate > month.date)
      .sortBy((interval) => interval.date);

    // Search the months for the planned spending item with the same filterId.
    intervalsToSum.forEach((f2sMonth) => {
      const newItem = f2sMonth?.plannedSpendingItems?.find((plannedItem) => {
        return plannedItem.filterId === filterId;
      });
      plannedItemList.push(newItem);
    });

    return plannedItemList;
  },
);

// Search through the future intervals to grab the correct plannedSpending items by filterId. They should be after todaysDate, but BEFORE the STARTDATE.
export const getPlannedSpendingItemListBeyondDateByFilterId = createSelector(
  [
    getFutureFreeToSpendIntervalsIncludingToday,
    getPlannedSpendingItemsForDate,
    (_state, startDate: string) => startDate,
    dateSelectors.getCurrentDateString,
  ],
  (intervals, plannedItemsList, myStartDate, currentDate) => {
    const filterIdList = plannedItemsList.map((item) => item?.filterId);
    return filterIdList?.map((filterId: string) => {
      const startDate = myStartDate || DateTime.fromISO(currentDate).startOf('month').toISODate() || '';
      const plannedItemList: (PlannedSpendingItem | undefined)[] = [];

      const intervalsToSum = intervals.filter((month) => startDate > month.date).sortBy((interval) => interval.date);

      // Search the months for the planned spending item with the same filterId.
      intervalsToSum.forEach((f2sMonth) => {
        const newItem = f2sMonth?.plannedSpendingItems?.find((plannedItem) => {
          return plannedItem.filterId === filterId;
        });
        plannedItemList.push(newItem);
      });

      return plannedItemList;
    });
  },
);

export const getPlannedSpendingDisplayCategoryByFilterId = createSelector(
  [
    categoriesSelectors.getCategoriesById,
    (state, filterId: string | undefined) => filtersSelectors.getFilter(state, filterId || ''),
  ],
  (categories, filter) => {
    const parentCoaId = categoriesSelectors.getParentFromCoaListFunction(categories, filter);
    const parentCoaIdToSubmit = filter?.filterItems?.[0]?.type === 'CHARTOFACCOUNT' ? parentCoaId || '' : '';
    const category = categories.get(parentCoaIdToSubmit);
    return category;
  },
);

// This uses QCS calculations for the past and present, AND client calculations for the future!
export const getPlannedSpendingItemRolloverInfo = createSelector(
  [
    (state, _plannedId: string, filterId: string, startDate: string) =>
      getPlannedSpendingItemsBeyondDateByFilterId(state, startDate, filterId), // Get all items to sum AFTER today's date
    (_state, _plannedId: string, _filterId: string, startDate: string) => startDate, // Get the selected date
    (state, _plannedId, filterId, startDate) =>
      getPlannedItemByDateAndFilterId(
        state,
        DateTime.fromISO(startDate).startOf('month').minus({ months: 1 }).toISODate() ?? '',
        filterId,
      ), // get the previous months item
    (state, plannedId: string, _filterId: string, startDate: string) =>
      getPlannedItemByDateAndId(state, startDate, plannedId), // get the item for the SELECTED date
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'spendingPlanRollForward'), // Do we calculate future dates rollovers?
    dateSelectors.getCurrentDateString,
    (_state, _plannedId: string, _filterId: string, _startDate: string, onlyCalculated?: boolean) => onlyCalculated,
  ], // We ignore the reset value and ONLY get the calculated value!!
  (items, myStartDate, prevPlannedItem, plannedItem, futureFeatureFlag, currentDate, onlyCalculated) =>
    getRolloverForItem(
      items,
      myStartDate,
      prevPlannedItem,
      plannedItem,
      futureFeatureFlag,
      currentDate,
      onlyCalculated,
    ),
);

// This uses QCS calculations for the past and present, AND client calculations for the future!
export const getPlannedSpendingItemListRolloverInfo = createSelector(
  [
    getPlannedSpendingItemListBeyondDateByFilterId, // Get all items to sum AFTER the today's date // returns and array of immutableMaps
    (_state, startDate: string) => startDate, // Get the selected date
    (state, startDate: string) =>
      getPlannedSpendingItemsForDate(
        state,
        DateTime.fromISO(startDate).startOf('month').minus({ months: 1 }).toISODate() as string,
      ), // get the previous months items
    getPlannedSpendingItemsForDate, // get the items for the SELECTED date
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'spendingPlanRollForward'), // Do we calculate future dates rollovers?
    dateSelectors.getCurrentDateString,
  ],
  (itemList, myStartDate, prevPlannedItemList, plannedItemList, futureFeatureFlag, currentDate) =>
    plannedItemList.map((plannedItem) => {
      const prevPlannedItem = prevPlannedItemList.find((prevItem) => prevItem?.filterId === plannedItem?.filterId);
      const items = itemList?.find((myItem) => myItem?.[0]?.filterId === plannedItem?.filterId);
      return [
        plannedItem?.id,
        getRolloverForItem(items, myStartDate, prevPlannedItem, plannedItem, futureFeatureFlag, currentDate, false),
      ];
    }),
);

export const getCalculatedOnlyPlannedSpendingItemRolloverInfo = createSelector(
  (state, plannedId: string, filterId: string, startDate: string) =>
    getPlannedSpendingItemRolloverInfo(state, plannedId, filterId, startDate, true),
  (result) => result,
);

export const getPlannedSpendingItemsByF2SId = createSelector(
  [
    getFreeToSpendById,
    (state) => preferencesV2Selectors.getPreferenceValue(state, 'sSortPlannedItemsByAmount'),
    filtersSelectors.getFilters,
    categoriesSelectors.getCategoriesById,
  ],
  (freeToSpend, sortByAmount, filters, categoriesById) =>
    sortPlannedSpendingItems(freeToSpend?.plannedSpendingItems, sortByAmount, filters, categoriesById),
);

export const getTotalTargetValueForPlannedSpending = createSelector(getPlannedSpendingItemsForDate, (plannedItems) => {
  let total = 0;
  plannedItems?.forEach((item) => {
    if (item.overwrittenTargetAmount === 0 || item.overwrittenTargetAmount) {
      total += item.overwrittenTargetAmount;
    } else {
      total += item.targetAmount;
    }
  });
  return Number(total.toFixed(2));
});

export const getCalculatedPlannedSpending = (state: State, freeToSpendId: string) => {
  const spendingPlan = getFreeToSpendById(state, freeToSpendId);
  return spendingPlan ? getPlannedSpendingAmount(state, spendingPlan) : 0;
};

export const getPlannedCoaIds = createSelector(
  getPlannedSpendingItemsForDate,
  filtersSelectors.getFilters,
  (plannedItems, filters) =>
    plannedItems
      ?.flatMap((item) => {
        const firstFilterItem = filters?.get(item.filterId)?.filterItems?.[0];
        if (!firstFilterItem) return [];
        switch (firstFilterItem.type) {
          case 'CHARTOFACCOUNT':
            return firstFilterItem.chartOfAccounts?.map((coa) => coa.id);
          default:
            return [];
        }
      })
      .toSet() || emptyStringSet,
);

const getCategories = (
  plannedItems: PlannedSpendingItems | undefined,
  filters: any,
  categories: any,
  includeUncategorized: boolean,
) => {
  const catMap: [string, PlannedSpendingConflict][] = []; // Consolidating Planned Items and COA information.
  let catFilter;
  // Create a map from category id to name and parent id to quickly check if parent id already exists in planned items
  plannedItems?.forEach((item) => {
    // get filter of planned item
    catFilter = filters.get(item.filterId);
    // get the cat id from filter
    const id = categoriesSelectors.getParentFromCoaListFunction(catFilter, categories);
    // get the cat
    const cat = categories.get(id);
    // loop through filter items
    catFilter?.filterItems[0]?.chartOfAccounts?.map((x: ChartOfAccountNode) => {
      const catX = categories.get(x.id);
      if (item.name && catX && x.id) {
        catMap.push([x.id, { name: item.name, parentId: catX?.parentId, plannedId: item.id, filterId: item.filterId }]);
      } else if (cat && catX && x.id) {
        const { name } = cat;
        const { parentId } = catX;
        catMap.push([x.id, { name, parentId, plannedId: item.id, filterId: item.filterId }]);
      } else if (includeUncategorized && id === '0' && x.id) {
        catMap.push([x.id, { name: 'Uncategorized', parentId: '0', plannedId: '0', filterId: '0' }]);
      }
      return x;
    });
  });
  return ImmutableMap(catMap);
};

export const getPlannedSpendingCoas = createSelector(
  [
    getPlannedSpendingItemsForDate,
    filtersSelectors.getFilters,
    categoriesSelectors.getCategoriesById,
    (_state, _startDate: string, includeUncategorized: boolean) => includeUncategorized,
  ],
  (plannedItems, filters, categories, includeUncategorized) =>
    getCategories(plannedItems, filters, categories, includeUncategorized),
);

const getExistingFutureResources = createSelector(
  [getResourcesById, (_state, date: string) => date],
  (resources, date) =>
    resources?.filter((resource) => resource?.date && resource.date > date && !resource.isDeleted) || ImmutableMap(),
);

export const getFuturePlannedSpendingCoas = createSelector(
  [
    getExistingFutureResources,
    filtersSelectors.getFilters,
    categoriesSelectors.getCategoriesById,
    (_state, _startDate: unknown, includeUncategorized: boolean) => includeUncategorized,
  ],
  (futureSpendingPlans, filters, categories, includeUncategorized) =>
    futureSpendingPlans.flatMap((spendingPlan) =>
      getCategories(spendingPlan.plannedSpendingItems, filters, categories, includeUncategorized),
    ),
);

export const earliestIntervalDate = createSelector(getResourcesById, (intervals) => {
  const minInterval =
    intervals &&
    intervals
      .toList() // @ts-expect-error TS2362, TS2363 // TS doesn't like subtracting dates
      .min((interval1, interval2) => DateTime.fromISO(interval1.date) - DateTime.fromISO(interval2.date));
  return minInterval && DateTime.fromISO(minInterval.date as string);
});

// has splitItem support + no filtering excluded ones
export const getTransactionItemsForPlannedSpendingItemIncludingExcluded = createWeakMapSelector.withTypes<State>()(
  [
    (state, props: { plannedSpending: PlannedSpendingItem | null }) =>
      filtersSelectors.getFilters(state)?.get(props?.plannedSpending?.filterId || ''),
    (state, props: { date: string }) => getTransactionsForMonth(state, props.date),
    getScheduledTxnsById,
    categoriesSelectors.getIncomeCOAIds,
  ],
  (filter, transactions, scheduledTxnsById, incomeCOAIds) => {
    const itemsArray: TransactionItem[] = [];

    if (filter) {
      // @ts-expect-error ts(2339) // thinks it can be not a COA type (but PSIs are always COA type)
      const coaIdArray = filter?.filterItems?.[0]?.chartOfAccounts?.map((coa) => coa.id);

      transactions.forEach((txn: TransactionProps) => {
        if (!txn.isBill && !txn.isSubscription) {
          if (!shouldIncludeTxnInPlannedSpending({ txn, scheduledTxnsById, incomeCOAIds })) {
            return;
          }
          if (transactionsUtils.isSplitTxn(txn)) {
            txn.split?.items?.forEach((splitItem) => {
              if (splitItem.coa?.id && coaIdArray.includes(splitItem.coa?.id)) {
                itemsArray.push(
                  mkTransactionItem({
                    transaction: txn as TransactionRecord,
                    splitItem: splitItem as RecordOf<SplitItemProps>,
                    item: {
                      ...txn,
                      ...(Record.isRecord(splitItem) ? splitItem?.toObject() : splitItem),
                    },
                  }),
                );
              }
            });
          } else if (txn.coa?.id && coaIdArray?.includes(txn.coa?.id)) {
            itemsArray.push(
              mkTransactionItem({
                transaction: txn as TransactionRecord,
                item: txn,
              }),
            );
          }
        }
      });
    }
    return ImmutableList<TransactionItem>(itemsArray);
  },
);

export const getExcludedTxnQtyForPlannedItem = createSelector(
  [getTransactionItemsForPlannedSpendingItemIncludingExcluded],
  (transactions) => transactions.filter((txn) => txn?.item?.isExcludedFromF2S).size,
);

// Filter out excluded ones
export const getTransactionItemsForPlannedSpendingItem = createWeakMapSelector.withTypes<State>()(
  [
    getTransactionItemsForPlannedSpendingItemIncludingExcluded,
    (state, props: { plannedSpending: PlannedSpendingItem | null }) => props.plannedSpending,
  ],
  (transactions, psi) =>
    psi?.isHideExcludedFromUi ? transactions.filter((txn) => !txn?.item?.isExcludedFromF2S) : transactions,
);

type CalculatedPlannedSpendingItem = Pick<
  PlannedSpendingItem,
  'rolloverType' | 'overwrittenTargetAmount' | 'targetAmount'
> & { calculatedIncluded: number | undefined };
type CalculatedPlannedSpending = { [id: string]: CalculatedPlannedSpendingItem };

const shouldCalculatePlannedSpending = (state: State, date: string): boolean => {
  const effectiveDate = date || dateSelectors.getStartOfMonthDate(state);
  const dateOnAfter = transactionsSelectors.getDateOnAfter(state);
  return Boolean(
    featureFlagsSelectors.getFeatureFlag(state, 'spendingPlanClientCalc') &&
      (!dateOnAfter || dateOnAfter < effectiveDate),
  );
};

const equalCalculatedPlannedSpending = (a: CalculatedPlannedSpending, b: CalculatedPlannedSpending): boolean => {
  const ids = Object.keys(a);
  return (
    ids.length === Object.keys(b).length &&
    ids.every((id) => {
      if (!Object.prototype.hasOwnProperty.call(b, id)) return false;
      const previous = a[id];
      const next = b[id];
      return (
        Object.is(previous.rolloverType, next.rolloverType) &&
        Object.is(previous.calculatedIncluded, next.calculatedIncluded) &&
        Object.is(previous.overwrittenTargetAmount, next.overwrittenTargetAmount) &&
        Object.is(previous.targetAmount, next.targetAmount)
      );
    })
  );
};

export const getCalculatedPlannedSpendingObject = createSelector(
  [
    getUnsortedPlannedSpendingItemsForDate,
    (state, date: string) => date || dateSelectors.getStartOfMonthDate(state),
    (state: State, date: string) =>
      shouldCalculatePlannedSpending(state, date) ? filtersSelectors.getFilters(state) : emptyFilters,
    (state: State, date: string) =>
      shouldCalculatePlannedSpending(state, date) ? getSummaryTransactionsForMonth(state, date) : emptyTxnMap,
    (state: State, date: string) =>
      shouldCalculatePlannedSpending(state, date) ? getScheduledTxnsById(state) : emptyScheduledTransactions,
    (state: State, date: string) =>
      shouldCalculatePlannedSpending(state, date) ? categoriesSelectors.getIncomeCOAIds(state) : emptyStringSet,
    shouldCalculatePlannedSpending,
  ],
  (
    plannedItemsForDate,
    date,
    filters,
    transactions,
    scheduledTxnsById,
    incomeCOAIds,
    calculateClientAmounts,
  ): CalculatedPlannedSpending => {
    const retObj: CalculatedPlannedSpending = {};
    plannedItemsForDate?.forEach((item) => {
      retObj[item.id] = {
        rolloverType: item?.rolloverType,
        calculatedIncluded: calculateClientAmounts
          ? getValueFromFilterAndTransactions({
              filters,
              transactions,
              filterId: item.filterId,
              date,
              scheduledTxnsById,
              incomeCOAIds,
            })
          : item.calculatedSpentAmount,
        overwrittenTargetAmount: item.overwrittenTargetAmount,
        targetAmount: item.targetAmount,
      };
    });
    return retObj;
  },
  {
    memoize: resettableLruMemoize,
    memoizeOptions: {
      resultEqualityCheck: equalCalculatedPlannedSpending,
      maxSize: 10,
    },
  },
);

export const getCalculatedValueForPlannedSpending = createSelector(
  [
    (state, date: string) => getPlannedSpendingItemsForDate(state, date),
    (state, date: string) => getCalculatedPlannedSpendingObject(state, date),
  ],
  (plannedItems, plannedItemObj) =>
    plannedItems?.reduce(
      (accumulator, planned) => accumulator + (plannedItemObj?.[planned?.id]?.calculatedIncluded ?? 0),
      0,
    ) || 0,
);

export const getPlannedSpendingAmount = (state: State, freeToSpend: FreeToSpend) => {
  const spendingPlanClientCalc = featureFlagsSelectors.getFeatureFlag(state, 'spendingPlanClientCalc');
  if (!spendingPlanClientCalc) {
    return freeToSpend?.calculatedPlannedSpendingAmount;
  }
  return getPlannedSpendingAmountSelector(state, freeToSpend);
};

export const getPlannedSpendingAmountSelector = createSelector(
  [
    (state, freeToSpend: FreeToSpend | undefined) => freeToSpend,
    (state, freeToSpend: FreeToSpend | undefined) => getPlannedSpendingItemsForDate(state, freeToSpend?.date || ''),
    (state, freeToSpend: FreeToSpend | undefined) => getCalculatedPlannedSpendingObject(state, freeToSpend?.date || ''),
  ],
  (freeToSpend, plannedItems, plannedItemObj) => {
    if (!freeToSpend) return 0;
    return (
      plannedItems?.reduce((accumulator, planned) => {
        const item = plannedItemObj[planned.id];

        const targetAmount = item.overwrittenTargetAmount ?? item.targetAmount;
        const calculatedIncluded = item.calculatedIncluded ?? 0;

        if (validRolloverStates.includes(item?.rolloverType)) {
          return accumulator + targetAmount * -1;
        }

        // need to account for positive spending, so cannot do math.abs()
        if (targetAmount > calculatedIncluded * -1) {
          return accumulator + targetAmount * -1;
        }

        return accumulator + calculatedIncluded;
      }, 0) || 0
    );
  },
);

export const getPlannedSpendingAmountForDate = (state: State, date: string, id: string) =>
  getCalculatedPlannedSpendingObject(state, date)?.[id]?.calculatedIncluded || 0;

export const isPlannedOverSpent = createSelector(
  getCalculatedPlannedSpendingObject,
  getPlannedSpendingItemsForDate,
  (plannedObj, plannedItems) =>
    plannedItems?.some(
      (item) =>
        // Multiplying by -1 because the value is already negative, and we must compare it to positive values.
        Number(plannedObj[item.id]?.calculatedIncluded) * -1 > (item.overwrittenTargetAmount ?? item.targetAmount) &&
        !validRolloverStates.includes(item?.rolloverType),
    ),
);

export const isPlannedOverSpentIncludingRollovers = createSelector(
  [
    getCalculatedPlannedSpendingObject,
    getPlannedSpendingItemsForDate,
    (state, startDate: string) => getPlannedSpendingItemListRolloverInfo(state, startDate),
  ],
  (plannedObj, plannedItems, rolloverList: any) =>
    plannedItems?.some((item) => {
      const rollover: number =
        Number(rolloverList?.find(([key, _rollover]: [key: string, _rollover: any]) => item?.id === key)[1]) || 0;
      // Multiplying by -1 because the value is already negative, and we must compare it to positive values.
      let spentAmount = Number(plannedObj[item.id]?.calculatedIncluded) * -1;
      let targetAmount = item.overwrittenTargetAmount ?? item.targetAmount;

      if (rollover < 0) {
        spentAmount -= rollover;
      } else {
        targetAmount += rollover;
      }
      return spentAmount > targetAmount;
    }),
);

export const getPlannedOverSpentQty = createSelector(
  getCalculatedPlannedSpendingObject,
  getPlannedSpendingItemsForDate,
  (plannedObj, plannedItems) =>
    plannedItems?.filter(
      (item) =>
        // Multiplying by -1 because the value is already negative, and we must compare it to positive values
        Number(plannedObj[item.id]?.calculatedIncluded) * -1 > (item.overwrittenTargetAmount ?? item.targetAmount) &&
        !validRolloverStates.includes(item?.rolloverType),
    )?.size,
);

export const getAverageSpendingForCategory = createSelector(getTransactionsForCoa, (transactions) => {
  const cacheKey = selectorsHelpers.getOrCreateStringFromObject(
    'planned-average',
    { transactions },
    6,
    selectorsHelpers.memoizeWithKeyRetrieveCache,
  );

  return selectorsHelpers.memoizeWithKey(
    'planned-average',
    () => {
      if (!transactions) return { averageSpending: 0, numMonths: 0 };
      const { sum, numMonths } = transactions;
      return { averageSpending: numMonths <= 0 ? 0 : sum / numMonths, numMonths };
    },
    cacheKey,
    6,
  );
});

// ===============================================================================================================================================================================
// ==================================================================== PLANNED SPENDING SELECTORS END HERE ====================================================================
// ===============================================================================================================================================================================

// ========== SPENDING PLAN SELECTORS START HERE =============

/** Load state shared by the Spending Plan breakdown calculations. */
const getBreakdownLoadPending = (state: State): boolean =>
  accountsSelectors.getLoadPending(state) ||
  categoriesSelectors.getLoadPending(state) ||
  transactionsSelectors.getLoadPending(state) ||
  getLoadPending(state);

const otherSpendingInputs = (
  transactionsForMonth: MonthTransactionsSelector,
): [
  MonthTransactionsSelector,
  (state: State, date: string) => ReturnType<typeof getPlannedCoaIds>,
  typeof categoriesSelectors.getIncomeCOAIds,
  typeof getScheduledTxnsById,
  typeof getBreakdownLoadPending,
] => [
  transactionsForMonth,
  (state, date) =>
    getPlannedCoaIds(
      // returns immutable set
      state,
      date ?? (dateSelectors.getCurrentDate(state).startOf('month').toISODate() as string),
    ),
  categoriesSelectors.getIncomeCOAIds,
  getScheduledTxnsById,
  getBreakdownLoadPending,
];

export const getOtherSpendingTxnsWithSplitsSelector = createSelector(
  otherSpendingInputs(getTransactionsForMonth),
  (transactions, plannedCats, incomeCats, scheduledTxnsById, loadPending) => {
    if (loadPending) {
      return { listTxns: emptyItemsList, chartTxns: emptyTxnList, billOverflowAmount: 0 };
    }
    const matchedTxnIdsFromReminders = transactionsUtils.getMatchedTxnIdsFromReminders(transactions.values());

    const categoriesToExclude = plannedCats.concat(incomeCats);
    const hasCoaToDisplay = (item: TransactionProps | SplitItemProps) =>
      !categoriesToExclude.has(item?.coa?.id ?? '') &&
      !transactionsUtils.isTransferOrCreditCardPaymentTxn(item) &&
      !item?.businessId;

    const chart: TransactionRecord[] = [];
    const itemArray: TransactionItem[] = [];
    let ignoredTxnQuantity = 0;
    let billOverflowAmount = 0;
    transactions.forEach((txn) => {
      const matchedSplitSeriesBucket = getReminderSeriesBucket({
        txn,
        scheduledTxnsById,
        incomeCOAIds: incomeCats,
      });
      const keepMatchedOverflowInBills = matchedSplitSeriesBucket !== null;
      const txnId = txn.id as string | undefined;
      if (txn.source !== 'SCHEDULED_TRANSACTION_PENDING' && txnId && matchedTxnIdsFromReminders.has(txnId)) {
        return;
      }

      if (!txn.isBill && !txn.isSubscription && !keepMatchedOverflowInBills) {
        const modelTxn = getScheduledModelTransaction(txn, scheduledTxnsById);
        const modelSplitItems =
          modelTxn && transactionsUtils.isSplitTxn(modelTxn)
            ? splitItemsToArray(modelTxn.split?.items ?? []).filter((item) => item)
            : [];

        // Only reminder instances should borrow split lines from the series model.
        // Posted/downloaded txns that are merely linked via stModelId do not inherit the template splits.
        if (
          !transactionsUtils.isSplitTxn(txn) &&
          shouldUseSeriesModelSplitItemsForTxn(txn) &&
          modelSplitItems.length > 0
        ) {
          if (transactionsUtils.isRealizedReminder(txn)) {
            let shouldPush = false;
            modelSplitItems.forEach((splitItem, index) => {
              // Derive transfer amounts (some linked transfer split lines can become 0); we still filter out transfers below.
              const splitTransferId = splitItem.transfer?.id ?? null;
              const otherSideForSplit = splitTransferId ? transactions.get(splitTransferId) : undefined;
              const effectiveSplitItemObj = getTransferSplitItemForF2S(splitItem, otherSideForSplit);
              if (!effectiveSplitItemObj) return;
              if (!hasCoaToDisplay(effectiveSplitItemObj)) return;
              const splitIsExcludedFromF2S = effectiveSplitItemObj.isExcludedFromF2S ?? txn.isExcludedFromF2S;

              if (splitIsExcludedFromF2S) {
                ignoredTxnQuantity++;
              }
              shouldPush = shouldPush || !splitIsExcludedFromF2S;

              const stableSplitId = transactionsUtils.getStableSplitItemId(txn, effectiveSplitItemObj, index);
              itemArray.push(
                mkTransactionItem({
                  transaction: txn as TransactionRecord,
                  splitItem: mkSplitItem({
                    ...(effectiveSplitItemObj as Partial<SplitItemProps>),
                    isExcludedFromF2S: splitIsExcludedFromF2S,
                  }),
                  item: {
                    ...txn.toObject(),
                    ...effectiveSplitItemObj,
                    isExcludedFromF2S: splitIsExcludedFromF2S,
                    id: stableSplitId,
                  },
                }),
              );
            });

            if (shouldPush) {
              chart.push(txn);
            }
            return;
          }
        }

        // split here
        if (transactionsUtils.isSplitTxn(txn)) {
          if (!transactionsUtils.isRealizedReminder(txn)) {
            return;
          }

          let shouldPush = false;
          txn.split?.items?.forEach((splitItem) => {
            if (hasCoaToDisplay(splitItem)) {
              const splitItemObj = (Record.isRecord(splitItem) ? splitItem.toObject() : splitItem) as SplitItemProps;
              const splitIsExcludedFromF2S = splitItemObj.isExcludedFromF2S ?? txn.isExcludedFromF2S;
              if (splitIsExcludedFromF2S) {
                ignoredTxnQuantity++;
              }
              shouldPush = shouldPush || !splitIsExcludedFromF2S;
              itemArray.push(
                mkTransactionItem({
                  transaction: txn as TransactionRecord,
                  splitItem: splitItem as RecordOf<SplitItemProps>,
                  item: {
                    ...txn.toObject(),
                    ...splitItemObj,
                    isExcludedFromF2S: splitIsExcludedFromF2S,
                  },
                }),
              );
            }
          });
          if (shouldPush) {
            chart.push(txn);
          }

          // non-split case
        } else if (hasCoaToDisplay(txn)) {
          if (txn.isExcludedFromF2S) {
            ignoredTxnQuantity++;
          }
          itemArray.push(
            mkTransactionItem({
              transaction: txn,
              item: txn.toObject(),
            }),
          );
          if (!txn.isExcludedFromF2S) {
            chart.push(txn);
          }
        }
      }

      if ((txn.isBill || txn.isSubscription || keepMatchedOverflowInBills) && transactionsUtils.isSplitTxn(txn)) {
        if (transactionsUtils.isRealizedReminder(txn)) {
          splitItemsToArray(txn.split?.items ?? []).forEach((splitItem, index) => {
            if (!isOverflowSplitItem(splitItem)) return;
            const amount = Number(splitItem.amount ?? 0);
            if (amount === 0) return;
            if (keepMatchedOverflowInBills) return;
            if (!txn.isExcludedFromF2S) {
              billOverflowAmount += amount;
              const stableSplitId = transactionsUtils.getStableSplitItemId(txn, splitItem, index);
              itemArray.push(
                mkTransactionItem({
                  transaction: txn as TransactionRecord,
                  splitItem: mkSplitItem(splitItem as Partial<SplitItemProps>),
                  item: { ...txn.toObject(), ...splitItem, id: stableSplitId },
                }),
              );
            }
          });
        }
      }
    });

    return {
      listTxns: ImmutableList<TransactionItem>(itemArray),
      chartTxns: ImmutableList<TransactionRecord>(chart),
      ignoredTxnQuantity,
      billOverflowAmount,
    };
  },
  {
    memoize: resettableLruMemoize,
    memoizeOptions: {
      resultEqualityCheck: (a, b) => {
        if (a === b) {
          return true;
        }
        if (a && b) {
          if (
            a.listTxns?.hashCode?.() === b.listTxns?.hashCode?.() &&
            a.chartTxns?.hashCode?.() === b.chartTxns?.hashCode?.() &&
            a?.ignoredTxnQuantity === b?.ignoredTxnQuantity &&
            a?.billOverflowAmount === b?.billOverflowAmount
          ) {
            return (
              a.listTxns.size === b.listTxns.size &&
              a.chartTxns.size === b.chartTxns.size &&
              a.listTxns.equals(b.listTxns) &&
              a.chartTxns.equals(b.chartTxns) &&
              a?.ignoredTxnQuantity === b?.ignoredTxnQuantity &&
              a?.billOverflowAmount === b?.billOverflowAmount
            );
          }
        }
        return false;
      },
      maxSize: 10,
    },
  },
);

// Amount reader: same inputs and calculation cache as the detail selector, fed memo-insensitive rows.
const getOtherSpendingForAmounts = createSelector(
  otherSpendingInputs(getSummaryTransactionsForMonth),
  getOtherSpendingTxnsWithSplitsSelector.memoizedResultFunc,
);

const getSpendingPlanDateById = (state: State, id: string): string =>
  getFreeToSpendById(state, id)?.date ?? (dateSelectors.getCurrentDate(state).startOf('month').toISODate() as string);

export const getOtherSpendingTxnsWithSplits = (state: State, id: string) => {
  return getOtherSpendingTxnsWithSplitsSelector(state, getSpendingPlanDateById(state, id));
};

export const getSpendingPlanGoalsBreakdownData = createSelector(
  [
    (state, freeToSpend: FreeToSpend) => freeToSpend,
    goalsSelectors.getResourcesById,
    (state: State, freeToSpend: FreeToSpend) => getSummaryTransactionsForMonthWithGoals(state, freeToSpend.date),
    (state, freeToSpend: FreeToSpend) => freeToSpend.date,
    (state) =>
      goalsSelectors.getLoadPending(state) ||
      accountsSelectors.getLoadPending(state) ||
      getLoadPending(state) ||
      transactionsSelectors.getLoadPending(state),
  ],
  (freeToSpend, goals, transactions, date, loadPending) => {
    if (loadPending) {
      return { includeData: emptyGoalsList, excludeData: emptyGoalsList, excludedAmount: 0, includedAmount: 0 };
    }

    let excludedAmount = 0;
    let includedAmount = 0;
    const includeDataArray: goalsTypes.Goal[] = [];
    const excludedDataArray: goalsTypes.Goal[] = [];

    // Would it make sense to do it elsewhere?
    const txnsByGoalId = transactions.groupBy((transaction) => transaction.accountId);

    goals?.forEach((goal: goalsTypes.Goal) => {
      const goalTxns = txnsByGoalId?.get(goal.accountId) as TxnMap;
      const hasTxns = Boolean(goalTxns?.first()); // size is not in Collection
      const monthlyContribution = hasTxns ? goalsUtils.getMonthlyContribution(goalTxns) : 0; // sum of actual contributions

      const actuallyCompleted = goal?.completedOn && goal.completedOn !== EPOCH_ISO;

      const goalStarted = date >= goal.startOn;
      if ((!goalStarted || actuallyCompleted) && monthlyContribution <= 0) {
        // only show contributions for goals that have expected contributions (so new goals don't retroactively change numbers)
        return;
      }

      let goalAmount = 0;
      if (!goalStarted) {
        // don't apply projected contributions in the past
        goalAmount = monthlyContribution; // only displays actual contributions
      } else {
        // Choose larger between the expected contribution amount or the current contribution amount
        goalAmount =
          !goal.contributionAmount || monthlyContribution > goal.contributionAmount
            ? monthlyContribution
            : goal.contributionAmount;
      }

      if (goal.isTakenFromF2S) {
        includeDataArray.push(goal);
        includedAmount += goalAmount;
      } else {
        excludedDataArray.push(goal);
        excludedAmount += goalAmount;
      }
    });

    return {
      includeData: ImmutableList(includeDataArray).sort((goal1, goal2) => goal1.name?.localeCompare(goal2.name)),
      excludeData: ImmutableList(excludedDataArray).sort((goal1, goal2) => goal1.name?.localeCompare(goal2.name)),
      excludedAmount: excludedAmount * -1,
      includedAmount: includedAmount * -1,
      overwrittenAmount: isNumber(freeToSpend.overwrittenGoalsAmount) ? freeToSpend.overwrittenGoalsAmount : undefined,
    };
  },
);

let breakdownCalcNumber = 0;
/** Read a breakdown selector and report its calculation time only when the combiner actually ran. */
const getSpendingPlanBreakdownWithTracking = <Result>(
  state: State,
  freeToSpend: FreeToSpend,
  selector: (state: State, freeToSpend: FreeToSpend) => Result,
): Result => {
  const startTime = performance.now();
  const calcNumber = breakdownCalcNumber + 1;
  const clientCalc = selector(state, freeToSpend);
  if (calcNumber === breakdownCalcNumber) {
    tracker.track(tracker.events.spendingPlanClientCalc, {
      calcTime: performance.now() - startTime,
      type: 'breakdown',
    });
  }
  return clientCalc;
};

// returns txnItems instead of txnRecords in breakdownLists
export const getSpendingPlanBreakdownDataWithSplits = (
  state: State,
  freeToSpend: FreeToSpend,
): ReturnType<typeof getSpendingPlanBreakdownDataWithSplitsSelector> =>
  getSpendingPlanBreakdownWithTracking(state, freeToSpend, getSpendingPlanBreakdownDataWithSplitsSelector);

const getSpendingPlanBreakdownDataWithSplitsSelector = createSelector(
  [
    (_state, freeToSpend: FreeToSpend) => freeToSpend,
    (state, freeToSpend: FreeToSpend) => getTransactionsForMonth(state, freeToSpend.date),
    categoriesSelectors.getIncomeCOAIds,
    transactionsSelectors.getDateOnAfter,
    (state) =>
      accountsSelectors.getLoadPending(state) ||
      categoriesSelectors.getLoadPending(state) ||
      transactionsSelectors.getLoadPending(state) ||
      getLoadPending(state),
  ],
  (freeToSpend, transactions, incomeCoas, earliestDate, loadPending) => {
    breakdownCalcNumber += 1;
    if (loadPending) {
      return {
        income: {
          includeData: emptyItemsList,
          excludeData: emptyItemsList,
          excludedAmount: 0,
          includedAmount: freeToSpend.calculatedIncomeAmount ?? 0,
        },
        bills: {
          includeData: emptyItemsList,
          excludeData: emptyItemsList,
          excludedAmount: 0,
          includedAmount: freeToSpend.calculatedBillsAmount ?? 0,
        },
        transfers: {
          includeData: emptyItemsList,
          excludeData: emptyItemsList,
          excludedAmount: 0,
          includedAmount: freeToSpend.calculatedTransferAmount ?? 0,
        },
        subscriptions: {
          includeData: emptyItemsList,
          excludeData: emptyItemsList,
          excludedAmount: 0,
          includedAmount: freeToSpend.calculatedSubscriptionsAmount ?? 0,
        },
      };
    }
    const txnsIncomeIncludedArray: TransactionItem[] = [];
    const txnsIncomeExcludedArray: TransactionItem[] = [];
    const txnsBillsIncludedArray: TransactionItem[] = [];
    const txnsBillsExcludedArray: TransactionItem[] = [];
    const txnsTransfersIncludedArray: TransactionItem[] = [];
    const txnsTransfersExcludedArray: TransactionItem[] = [];
    const txnsSubscriptionsIncludedArray: TransactionItem[] = [];
    const txnsSubscriptionsExcludedArray: TransactionItem[] = [];

    const bucketizeTxnItem = (txnItem: TransactionItem) => {
      const { transaction, item, splitItem } = txnItem;
      const isTransfer = transactionsUtils.isTransferOrCreditCardPaymentTxn(splitItem || transaction);
      if (isTransfer && transaction.subtype !== 'GOAL_SPENT') {
        // ===== TRANSFER ==========
        if (!transaction.isExcludedFromF2S) {
          txnsTransfersIncludedArray.push(txnItem);
        } else {
          txnsTransfersExcludedArray.push(txnItem);
        }
      } else if (incomeCoas.has(item?.coa?.id as string)) {
        // ===== INCOME =====
        if (!transaction.isExcludedFromF2S) {
          txnsIncomeIncludedArray.push(txnItem);
        } else {
          txnsIncomeExcludedArray.push(txnItem);
        }
      } else if (transaction.isBill) {
        // ==== BILL ======
        if (!transaction.isExcludedFromF2S) {
          txnsBillsIncludedArray.push(txnItem);
        } else {
          txnsBillsExcludedArray.push(txnItem);
        }
      } else if (transaction.isSubscription) {
        // ===== SUBSCRIPTION =======
        if (!transaction.isExcludedFromF2S) {
          txnsSubscriptionsIncludedArray.push(txnItem);
        } else {
          txnsSubscriptionsExcludedArray.push(txnItem);
        }
      }
    };

    transactions.forEach((txn) => {
      if (transactionsUtils.isSplitTxn(txn)) {
        txn.split?.items?.forEach((splitItem) => {
          const item = {
            ...txn.toObject(),
            ...(Record.isRecord(splitItem) ? splitItem?.toObject() : splitItem),
          };
          if (item?.id) {
            const transactionItem = mkTransactionItem({
              transaction: txn,
              splitItem: splitItem as RecordOf<SplitItemProps>,
              item,
            });
            bucketizeTxnItem(transactionItem);
          }
        });
      } else {
        const item = txn.toObject();
        if (item?.id) {
          const transactionItem = mkTransactionItem({
            transaction: txn,
            item,
          });
          bucketizeTxnItem(transactionItem);
        }
      }
    });

    const txnsIncomeIncluded = ImmutableList(txnsIncomeIncludedArray);
    const txnsIncomeExcluded = ImmutableList(txnsIncomeExcludedArray);
    const txnsBillsIncluded = ImmutableList(txnsBillsIncludedArray);
    const txnsBillsExcluded = ImmutableList(txnsBillsExcludedArray);
    const txnsTransfersIncluded = ImmutableList(txnsTransfersIncludedArray);
    const txnsTransfersExcluded = ImmutableList(txnsTransfersExcludedArray);
    const txnsSubscriptionsIncluded = ImmutableList(txnsSubscriptionsIncludedArray);
    const txnsSubscriptionsExcluded = ImmutableList(txnsSubscriptionsExcludedArray);

    return {
      income: {
        includeData: txnsIncomeIncluded,
        excludeData: txnsIncomeExcluded,
        excludedAmount: getItemsSum(txnsIncomeExcludedArray),
        includedAmount:
          !earliestDate || earliestDate < freeToSpend.date
            ? getItemsSum(txnsIncomeIncludedArray)
            : freeToSpend.calculatedIncomeAmount || 0,
      },
      bills: {
        includeData: txnsBillsIncluded,
        excludeData: txnsBillsExcluded,
        excludedAmount: getItemsSum(txnsBillsExcludedArray),
        includedAmount:
          !earliestDate || earliestDate < freeToSpend.date
            ? getItemsSum(txnsBillsIncludedArray)
            : freeToSpend.calculatedBillsAmount || 0,
      },
      transfers: {
        includeData: txnsTransfersIncluded,
        excludeData: txnsTransfersExcluded,
        excludedAmount: getItemsSum(txnsTransfersExcludedArray),
        includedAmount:
          !earliestDate || earliestDate < freeToSpend.date
            ? getItemsSum(txnsTransfersIncludedArray)
            : freeToSpend.calculatedTransferAmount || 0,
      },
      subscriptions: {
        includeData: txnsSubscriptionsIncluded,
        excludeData: txnsSubscriptionsExcluded,
        excludedAmount: getItemsSum(txnsSubscriptionsExcludedArray),
        includedAmount:
          !earliestDate || earliestDate < freeToSpend.date
            ? getItemsSum(txnsSubscriptionsIncludedArray)
            : freeToSpend.calculatedSubscriptionsAmount || 0,
      },
    };
  },
);

// # --     selector utility functions    -- #

// Sum business splits so parent rows can subtract them from their rollup amount.
const getBusinessSplitAmountTotal = (splitItems: SplitItemProps[]): number =>
  splitItems.reduce(
    (sum, splitItem) => (isBusinessSplitItem(splitItem) ? sum + Number(splitItem.amount ?? 0) : sum),
    0,
  );

// Use the single-item helper to zero business amounts for Spending Plan calculations.
const getSplitItemWithBusinessZeroed = (splitItem: SplitItemProps): SplitItemProps =>
  isBusinessSplitItem(splitItem)
    ? {
        ...splitItem,
        amount: 0,
      }
    : splitItem;

const getSplitItemsWithBusinessZeroed = (splitItems: SplitItemProps[]): SplitItemProps[] =>
  splitItems.map(getSplitItemWithBusinessZeroed);

// If every non-overflow split line is business, exclude the entire split from Spending Plan.
const isBusinessOnlySplit = (splitItems: SplitItemProps[]): boolean => {
  const nonOverflowItems = splitItems.filter((splitItem) => splitItem && !isOverflowSplitItem(splitItem));
  if (nonOverflowItems.length === 0) return false;
  return nonOverflowItems.every((splitItem) => isBusinessSplitItem(splitItem));
};

// Single-pass summary of a reminder's relevant (non-overflow, non-business, non-transfer) splits.
// Exclusion is resolved through getReminderSplitLineIsExcludedFromF2S so semantics stay aligned
// with getReminderIncludedNonTransferAmount and the per-split renderer — never inferred from amount.
type ReminderSplitExclusionSummary = {
  includedRelevantAmount: number;
  excludedRelevantAmount: number;
  relevantCount: number;
  hasExplicitIncludedRelevantLine: boolean;
  allRelevantNonTransferLinesExcluded: boolean;
};

const getReminderParentExclusionOverride = (
  exclusionSummary: ReminderSplitExclusionSummary | null | undefined,
): boolean | undefined => {
  if (!exclusionSummary || exclusionSummary.relevantCount === 0) return undefined;
  if (exclusionSummary.allRelevantNonTransferLinesExcluded) return true;
  return exclusionSummary.hasExplicitIncludedRelevantLine ? false : undefined;
};

const getReminderSplitExclusionSummary = ({
  txn,
  splitItems,
  rawSplitItems,
  modelSplitItems,
  scheduledTxnsById,
}: {
  txn: TransactionProps;
  splitItems: SplitItemProps[];
  rawSplitItems: SplitItemProps[];
  modelSplitItems?: SplitItemProps[];
  scheduledTxnsById: Parameters<typeof getScheduledModelTransaction>[1];
}): ReminderSplitExclusionSummary => {
  let includedRelevantAmount = 0;
  let excludedRelevantAmount = 0;
  let relevantCount = 0;
  let allExcluded = true;
  let hasExplicitIncludedRelevantLine = false;

  for (let i = 0; i < splitItems.length; i++) {
    const splitItem = splitItems[i];
    if (!splitItem) continue;
    if (isOverflowSplitItem(splitItem)) continue;
    if (isBusinessSplitItem(splitItem)) continue;
    if (transactionsUtils.isTransferOrCreditCardPaymentTxn(splitItem)) continue;
    relevantCount++;
    const explicitSplitExclusion =
      rawSplitItems[i]?.isExcludedFromF2S ?? splitItem.isExcludedFromF2S ?? modelSplitItems?.[i]?.isExcludedFromF2S;
    if (explicitSplitExclusion === false) {
      hasExplicitIncludedRelevantLine = true;
    }
    const isExcluded = getReminderSplitLineIsExcludedFromF2S({
      txn,
      splitItem,
      rawSplitItem: rawSplitItems[i],
      modelSplitItem: modelSplitItems?.[i],
      scheduledTxnsById,
    });
    const amount = Number(splitItem.amount ?? 0);
    if (isExcluded) {
      excludedRelevantAmount += amount;
    } else {
      includedRelevantAmount += amount;
      allExcluded = false;
    }
  }

  return {
    includedRelevantAmount,
    excludedRelevantAmount,
    relevantCount,
    hasExplicitIncludedRelevantLine,
    allRelevantNonTransferLinesExcluded: relevantCount > 0 && allExcluded,
  };
};

// # --     main bucketizing selector    -- #
type BreakdownInputs = [
  (state: State, freeToSpend: FreeToSpend) => FreeToSpend,
  (state: State, freeToSpend: FreeToSpend) => TxnMap,
  (state: State, freeToSpend: FreeToSpend) => ReturnType<typeof getPlannedCoaIds>,
  typeof getScheduledTxnsById,
  typeof categoriesSelectors.getIncomeCOAIds,
  typeof transactionsSelectors.getDateOnAfter,
  typeof getBreakdownLoadPending,
];
const breakdownInputs = (transactionsForMonth: MonthTransactionsSelector): BreakdownInputs => [
  (_state, freeToSpend) => freeToSpend,
  (state, freeToSpend) => transactionsForMonth(state, freeToSpend.date),
  (state, freeToSpend) => getPlannedCoaIds(state, freeToSpend.date),
  getScheduledTxnsById,
  categoriesSelectors.getIncomeCOAIds,
  transactionsSelectors.getDateOnAfter,
  getBreakdownLoadPending,
];

const getSpendingPlanBreakdownDataWithSplitsSelectorV2 = createSelector(
  breakdownInputs(getTransactionsForMonth),
  (freeToSpend, transactions, plannedCoaIds, scheduledTxnsById, incomeCoas, earliestDate, loadPending) => {
    breakdownCalcNumber += 1;
    if (loadPending) {
      return {
        income: {
          includeData: emptyItemsList,
          excludeData: emptyItemsList,
          excludedAmount: 0,
          includedAmount: freeToSpend.calculatedIncomeAmount ?? 0,
          overwrittenAmount: undefined,
        },
        bills: {
          includeData: emptyItemsList,
          excludeData: emptyItemsList,
          excludedAmount: 0,
          includedAmount:
            (freeToSpend?.calculatedBillsAmount || 0) +
            (freeToSpend?.calculatedTransferAmount || 0) +
            (freeToSpend?.calculatedSubscriptionsAmount || 0),
          overwrittenAmount: undefined,
        },
      };
    }

    const txnsIncomeIncludedArray: TransactionItem[] = [];
    const txnsIncomeExcludedArray: TransactionItem[] = [];
    const txnsBillsIncludedArray: TransactionItem[] = [];
    const txnsBillsExcludedArray: TransactionItem[] = [];
    const seenTxnItemIds = new Set<string>();
    const transferDirectLinks = new Map<string, Set<string>>();
    const transferIncludedIds = new Set<string>();
    const explicitScheduledSplitTransferCounterpartIds = new Set<string>();
    // Indexed on the first `transactions.forEach` below: for each `stModelId` of a pending reminder whose
    // instance carries a split with an ACCOUNT-typed transfer line, record the set of target accountIds
    // referenced by those transfer lines. Used by `isOtherSideOfModelSplitTransferTxn` to identify a
    // companion reminder (same `stModelId`, accountId present in this set) without touching the ST model.
    const primaryReminderTransferTargetsByStModelId = new Map<string, Set<string>>();

    // TODO: Re-use bucketize code used in getIncomeAfterBillsAndSavings?
    const getSTModelSplitItems = (txn: TransactionRecord): SplitItemProps[] => {
      const modelTxn = getScheduledModelTransaction(txn, scheduledTxnsById);
      if (!modelTxn || !transactionsUtils.isSplitTxn(modelTxn)) return [];

      return splitItemsToArrayOverflowLast(modelTxn.split?.items ?? []);
    };

    // Pending-reminder counterpart of `isOtherSideOfTransferTxnASplit`.
    //
    // A scheduled series whose model has a transfer split (e.g. a paycheck series that transfers a portion
    // into a 401K) materializes TWO pending reminder instances that share the same `stModelId`:
    //   - the "primary" reminder carries the split on the instance itself, and
    //   - one or more "companion" reminders on the transfer-target accounts are flat non-split transfer
    //     txns whose transfer linkage is expressed via `coa.type === ACCOUNT` pointing back at the source
    //     account (not via `txn.transfer`).
    //
    // The primary is the split parent: Spending Plan should render it (and its split rows). The companions
    // should be suppressed here — we render the paired "other-side" row for them via
    // `bucketizeOtherSideTransferRow` off the primary's transfer split. Without this suppression, each
    // companion gets re-projected through the same model splits and a second, spurious pair appears (e.g. a
    // self-transfer 401K -> 401K row in Bills Excluded).
    //
    // The generic helper `isOtherSideOfTransferTxnASplit` can't catch this case: it bails early when
    // `txn.transfer` is null, which it always is on a companion reminder. Here we identify the companion by
    // looking for a sibling primary reminder (same `stModelId`) whose instance splits include an
    // ACCOUNT-typed transfer line pointing back at this txn's account — mirroring the parent-lookup shape
    // of `isOtherSideOfTransferTxnASplit`. The `stModelId -> target accounts` index is built in the first
    // `transactions.forEach` below.
    const isOtherSideOfModelSplitTransferTxn = (
      txn: Pick<TransactionProps, 'source' | 'stModelId' | 'accountId'> | null | undefined,
    ): boolean => {
      if (txn?.source !== 'SCHEDULED_TRANSACTION_PENDING') return false;
      if (!txn.stModelId) return false;
      const txnAccountId = txn.accountId ? String(txn.accountId) : '';
      if (!txnAccountId) return false;
      const targets = primaryReminderTransferTargetsByStModelId.get(String(txn.stModelId));
      return Boolean(targets && targets.has(txnAccountId));
    };
    const isOtherSideOfScheduledModelSplitTransferTxn = (txn: TransactionRecord): boolean => {
      if (txn.source !== 'SCHEDULED_TRANSACTION_PENDING') return false;
      if (!txn.stModelId) return false;
      const linkedTxnId = (txn.transfer?.id ?? txn.transfer?.clientId ?? '') as string;
      if (!linkedTxnId) return false;

      const linkedTxn = transactions.get(linkedTxnId);
      if (linkedTxn?.stModelId && linkedTxn.stModelId !== txn.stModelId) return false;

      const modelTxn = getScheduledModelTransaction(txn, scheduledTxnsById);
      if (!modelTxn || !transactionsUtils.isSplitTxn(modelTxn)) return false;

      const txnIdentityKeys = new Set([txn.id, txn.clientId].filter(Boolean).map(String));
      const txnAccountId = txn.accountId ? String(txn.accountId) : '';
      const txnAmount = Number(txn.amount ?? 0);

      return splitItemsToArray(modelTxn.split?.items ?? []).some((splitItem) => {
        const splitTransferKeys = [splitItem.transfer?.id, splitItem.transfer?.clientId].filter(Boolean).map(String);
        if (splitTransferKeys.some((key) => txnIdentityKeys.has(key))) return true;

        return Boolean(
          splitItem.coa?.type === chartOfAccountsTypes.CoaTypeEnum.ACCOUNT &&
            txnAccountId &&
            String(splitItem.coa.id) === txnAccountId &&
            Number(splitItem.amount ?? 0) === txnAmount * -1,
        );
      });
    };

    const ensureTransferDirectLinkNode = (id: string): Set<string> => {
      const existing = transferDirectLinks.get(id);
      if (existing) return existing;

      const node = new Set<string>();
      transferDirectLinks.set(id, node);
      return node;
    };

    const addTransferConnection = ({
      selfId,
      linkedId,
      isIncluded,
    }: {
      selfId?: string | null;
      linkedId?: string | null;
      isIncluded: boolean;
    }): void => {
      const normalizedSelfId = selfId ? String(selfId) : null;
      const normalizedLinkedId = linkedId ? String(linkedId) : null;
      if (!normalizedSelfId || !normalizedLinkedId) return;

      ensureTransferDirectLinkNode(normalizedSelfId).add(normalizedLinkedId);
      ensureTransferDirectLinkNode(normalizedLinkedId).add(normalizedSelfId);
      if (isIncluded) {
        transferIncludedIds.add(normalizedSelfId);
      }
    };

    transactions.forEach((txn) => {
      const txnId = (txn.id ?? txn.clientId ?? '') as string;
      const txnLinkedTransferId = (txn.transfer?.id ?? txn.transfer?.clientId ?? '') as string;
      const txnIsIncluded = !(txn.isExcludedFromF2S ?? false);
      addTransferConnection({
        selfId: txnId,
        linkedId: txnLinkedTransferId,
        isIncluded: txnIsIncluded,
      });

      const splitItems = splitItemsToArrayOverflowLast(txn.split?.items ?? []);
      splitItems.forEach((splitItem, index) => {
        const splitId = transactionsUtils.getStableSplitItemId(txn, splitItem, index);
        const splitLinkedTransferId = (splitItem.transfer?.id ?? splitItem.transfer?.clientId ?? '') as string;
        const splitIsIncluded = !(splitItem.isExcludedFromF2S ?? txn.isExcludedFromF2S ?? false);
        addTransferConnection({
          selfId: splitId,
          linkedId: splitLinkedTransferId,
          isIncluded: splitIsIncluded,
        });
        if (txn.source === 'SCHEDULED_TRANSACTION_PENDING' && txn.stModelId) {
          [splitItem.transfer?.id, splitItem.transfer?.clientId].filter(Boolean).forEach((id) => {
            explicitScheduledSplitTransferCounterpartIds.add(String(id));
          });
        }
      });

      // Index primary reminder -> set of transfer-target accountIds (see
      // `isOtherSideOfModelSplitTransferTxn`). Only pending reminders with instance splits qualify as a
      // primary here; their companion siblings are flat non-split txns on the transfer-target accounts.
      if (txn.source === 'SCHEDULED_TRANSACTION_PENDING' && txn.stModelId && splitItems.length > 0) {
        const stModelId = String(txn.stModelId);
        splitItems.forEach((splitItem) => {
          if (splitItem?.coa?.type !== chartOfAccountsTypes.CoaTypeEnum.ACCOUNT) return;
          const targetAccountId = splitItem.coa.id ? String(splitItem.coa.id) : '';
          if (!targetAccountId) return;
          let targets = primaryReminderTransferTargetsByStModelId.get(stModelId);
          if (!targets) {
            targets = new Set<string>();
            primaryReminderTransferTargetsByStModelId.set(stModelId, targets);
          }
          targets.add(targetAccountId);
        });
      }

      const modelSplitItems = getSTModelSplitItems(txn);
      modelSplitItems.forEach((splitItem, index) => {
        const splitId = transactionsUtils.getStableSplitItemId(txn, splitItem, index);
        const splitLinkedTransferId = (splitItem.transfer?.id ?? splitItem.transfer?.clientId ?? '') as string;
        const splitIsIncluded = !(splitItem.isExcludedFromF2S ?? txn.isExcludedFromF2S ?? false);
        addTransferConnection({
          selfId: splitId,
          linkedId: splitLinkedTransferId,
          isIncluded: splitIsIncluded,
        });
        if (txn.source === 'SCHEDULED_TRANSACTION_PENDING' && txn.stModelId) {
          [splitItem.transfer?.id, splitItem.transfer?.clientId].filter(Boolean).forEach((id) => {
            explicitScheduledSplitTransferCounterpartIds.add(String(id));
          });
        }
      });
    });

    const shouldDisplayConnectedTransferInIncludedBucket = (txnItem: TransactionItem): boolean => {
      const ownId = String(txnItem.item?.id ?? txnItem.item?.clientId ?? '');
      const linkedTransferId = String(
        txnItem.item?.transfer?.id ??
          txnItem.item?.transfer?.clientId ??
          txnItem.splitItem?.transfer?.id ??
          txnItem.splitItem?.transfer?.clientId ??
          '',
      );
      const isExplicitScheduledSplitCounterpart = (() => {
        if (!ownId) return false;
        if (txnItem.transaction.source !== 'SCHEDULED_TRANSACTION_PENDING') return false;
        return explicitScheduledSplitTransferCounterpartIds.has(ownId);
      })();
      const candidateIds = [ownId, isExplicitScheduledSplitCounterpart ? '' : linkedTransferId].filter(Boolean);
      return candidateIds.some((id) => {
        if (transferIncludedIds.has(id)) return true;
        const directlyLinkedIds = transferDirectLinks.get(id);
        return Boolean(
          directlyLinkedIds && [...directlyLinkedIds].some((linkedId) => transferIncludedIds.has(linkedId)),
        );
      });
    };

    const bucketizeTxnItem = (txnItem: TransactionItem) => {
      const { transaction, item, splitItem } = txnItem;
      const isExcludedFromF2S = item?.isExcludedFromF2S ?? transaction.isExcludedFromF2S;
      const primarySeriesCoa = !splitItem ? getSeriesPrimaryCoa(transaction) : null;
      const bucketCoa = splitItem ? item?.coa : (item?.coa ?? primarySeriesCoa ?? transaction.coa);
      const bucketCoaId = bucketCoa?.id as string | undefined;
      const bucketCoaType = bucketCoa?.type as string | undefined;
      const derivedSplitSeriesBucket = getReminderSeriesBucket({
        txn: transaction,
        scheduledTxnsById,
        incomeCOAIds: incomeCoas,
      });
      const transferCheckCoa = splitItem
        ? splitItem?.coa
        : item?.transfer == null
          ? (bucketCoa ?? primarySeriesCoa)
          : primarySeriesCoa;
      const primarySeriesIsTransfer =
        transferCheckCoa?.type === chartOfAccountsTypes.CoaTypeEnum.ACCOUNT ||
        chartOfAccountsUtils.isTransferLikeBalanceAdjustmentCoa(transferCheckCoa);
      const isTransfer =
        Boolean(transaction.transfer?.id) ||
        ((splitItem ? transactionsUtils.isTransferOrCreditCardPaymentTxn(splitItem) : primarySeriesIsTransfer) &&
          transaction.subtype !== 'GOAL_SPENT');

      const key = String(item?.id ?? item?.clientId ?? '');
      if (key) {
        if (seenTxnItemIds.has(key)) return;
        seenTxnItemIds.add(key);
      }
      // Income bucketing must require COA type CATEGORY to avoid accidental ID collisions with ACCOUNT COAs.
      if (
        !isTransfer &&
        bucketCoaType === chartOfAccountsTypes.CoaTypeEnum.CATEGORY &&
        bucketCoaId &&
        incomeCoas.has(bucketCoaId)
      ) {
        // ===== INCOME =====
        if (!isExcludedFromF2S) {
          txnsIncomeIncludedArray.push(txnItem);
        } else {
          txnsIncomeExcludedArray.push(txnItem);
        }
      } else if (isTransfer) {
        // ==== TRANSFER ======
        if (!isExcludedFromF2S || shouldDisplayConnectedTransferInIncludedBucket(txnItem)) {
          txnsBillsIncludedArray.push(txnItem);
        } else {
          txnsBillsExcludedArray.push(txnItem);
        }
      } else if (transaction.isBill || transaction.isSubscription || derivedSplitSeriesBucket !== null) {
        // ==== BILL ======
        if (!isExcludedFromF2S) {
          txnsBillsIncludedArray.push(txnItem);
        } else {
          txnsBillsExcludedArray.push(txnItem);
        }
      }
    };

    const bucketizeOtherSideTransferRow = (
      sourceTxn: TransactionRecord,
      sourceSplitItem: SplitItemProps,
      otherSideTxn: TransactionRecord,
    ): void => {
      const otherModelTxn = getScheduledModelTransaction(otherSideTxn, scheduledTxnsById);
      const sourceAccountId = sourceTxn.accountId as string | undefined;

      if (sourceAccountId && otherModelTxn && transactionsUtils.isSplitTxn(otherModelTxn)) {
        const expectedAmountAbs = Math.abs(Number(sourceSplitItem.amount ?? 0));
        const otherModelSplitItems = splitItemsToArray(otherModelTxn.split?.items ?? []).filter((si) => si);
        const matchIdx = otherModelSplitItems.findIndex((si) => {
          if (!si) return false;
          if (si.coa?.type !== chartOfAccountsTypes.CoaTypeEnum.ACCOUNT) return false;
          if (String(si.coa.id) !== String(sourceAccountId)) return false;
          return Math.abs(Number(si.amount ?? 0)) === expectedAmountAbs;
        });

        if (matchIdx >= 0) {
          const matchedSplit = otherModelSplitItems[matchIdx]!;
          const synthesizedSplit: SplitItemProps = {
            ...matchedSplit,
            // Receiving side should be positive (target account row).
            amount: expectedAmountAbs,
          };

          const otherSideId = (otherSideTxn.id ?? otherSideTxn.clientId ?? '') as string;
          const item = {
            ...otherSideTxn.toObject(),
            ...synthesizedSplit,
            // Replace the raw other-side transfer row with the synthesized split-backed row.
            id: otherSideId || transactionsUtils.getStableSplitItemId(otherSideTxn, synthesizedSplit, matchIdx),
          };

          bucketizeTxnItem(
            mkTransactionItem({
              transaction: otherSideTxn,
              splitItem: mkSplitItem(synthesizedSplit as Partial<SplitItemProps>),
              item,
            }),
          );
          return;
        }
      }

      // Fallback: use the raw other-side txn row.
      bucketizeTxnItem(
        mkTransactionItem({
          transaction: otherSideTxn,
          item: otherSideTxn.toObject(),
        }),
      );
    };

    // When a real transaction is linked/matched to a scheduled reminder instance, Spending Plan should render the
    // reminder/split rows (planned view) and NOT the matched transaction row.
    const matchedTxnIdsFromReminders = transactionsUtils.getMatchedTxnIdsFromReminders(transactions.values());

    transactions.forEach((txn) => {
      const txnId = txn.id as string | undefined;
      if (txn.source !== 'SCHEDULED_TRANSACTION_PENDING' && txnId && matchedTxnIdsFromReminders.has(txnId)) {
        return;
      }

      // If this is the raw other-side transfer txn linked from a split line, suppress it.
      // We'll render a synthesized split-backed row for it instead.
      // Three flavors to cover:
      //   (a) real posted/downloaded other-side transfer txns, where `txn.transfer` points back at the split
      //       parent — caught by `isOtherSideOfTransferTxnASplit`.
      //   (b) scheduled-reminder companion instances with a shared scheduled transfer parent, where the flat
      //       generated target txn can be identified from the series model — caught by
      //       `isOtherSideOfScheduledModelSplitTransferTxn`.
      //   (c) scheduled-reminder companion instances, where `txn.transfer` is null and the linkage is only
      //       expressed via `coa.type === ACCOUNT` — caught by `isOtherSideOfModelSplitTransferTxn`.
      // Missing scheduled companion cases previously let reminders get re-projected through the model splits,
      // producing a spurious duplicate pair in Spending Plan (e.g. a self-transfer 401K -> 401K row).
      if (
        transactionsUtils.isTransferOrCreditCardPaymentTxn(txn) &&
        (isOtherSideOfTransferTxnASplit({ txn, transactionsById: transactions }) ||
          isOtherSideOfScheduledModelSplitTransferTxn(txn) ||
          isOtherSideOfModelSplitTransferTxn(txn))
      ) {
        return;
      }

      if (!transactionsUtils.isSplitTxn(txn)) {
        // Only reminder instances should borrow split lines from the series model.
        // Posted/downloaded txns that are merely linked via stModelId should keep their own row until they
        // actually receive transaction splits at match time.
        const modelSplitItems = getSTModelSplitItems(txn).filter((splitItem) => splitItem);
        if (shouldUseSeriesModelSplitItemsForTxn(txn) && modelSplitItems.length > 0) {
          // Entirely business splits should behave like business-category txns: excluded from Spending Plan.
          if (isBusinessOnlySplit(modelSplitItems)) return;
          const isReminder = txn.source === 'SCHEDULED_TRANSACTION_PENDING';
          const isReminderMatchedOrLinked = transactionsUtils.isTxnMatchedOrLinked(txn);
          // Subtract business split amounts from parent rows to keep totals free of business values.
          const businessSplitAmount = getBusinessSplitAmountTotal(modelSplitItems);
          const splitItemsForAmounts = getSplitItemsWithBusinessZeroed(modelSplitItems);
          const adjustedParentFallbackAmount =
            businessSplitAmount !== 0 ? Number(txn.amount ?? 0) - businessSplitAmount : Number(txn.amount ?? 0);

          if (isReminder) {
            if (!isReminderMatchedOrLinked) {
              // Pre-match: emit parent row, bucketed by primary split COA.
              // Income series should keep the full parent amount so Spending Plan totals/bucketing
              // stay aligned with the reminder's Income value before match.
              const reminderPrimaryCoa = getReminderParentCoa(modelSplitItems);
              const nonTransferAmount = getReminderIncludedNonTransferAmount({
                txn,
                splitItems: splitItemsForAmounts,
                rawSplitItems: modelSplitItems,
                scheduledTxnsById,
              });
              const exclusionSummary = getReminderSplitExclusionSummary({
                txn,
                splitItems: splitItemsForAmounts,
                rawSplitItems: modelSplitItems,
                scheduledTxnsById,
              });
              const isIncomeReminderParent =
                reminderPrimaryCoa?.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY &&
                incomeCoas.has(reminderPrimaryCoa.id ?? '');
              const parentAmount = exclusionSummary.allRelevantNonTransferLinesExcluded
                ? exclusionSummary.excludedRelevantAmount
                : isIncomeReminderParent
                  ? getIncomeReminderParentAmount({
                      splitItems: splitItemsForAmounts,
                      plannedCoaIds,
                      fallbackAmount: adjustedParentFallbackAmount,
                    })
                  : // For bills, fall back to the non-transfer amount minus business splits.
                    (nonTransferAmount ?? (businessSplitAmount !== 0 ? adjustedParentFallbackAmount : undefined));
              const baseObj = txn.toObject();
              const parentExclusionOverride = getReminderParentExclusionOverride(exclusionSummary);
              const parentObj = {
                ...baseObj,
                ...(reminderPrimaryCoa ? { coa: reminderPrimaryCoa } : undefined),
                ...(parentAmount !== undefined ? { amount: parentAmount } : undefined),
                // Avoid leaking a parent-level transfer link onto non-transfer rollup rows.
                ...(parentAmount !== undefined ? { transfer: null } : undefined),
                ...(parentExclusionOverride != null ? { isExcludedFromF2S: parentExclusionOverride } : undefined),
              };
              if (txn.id && shouldEmitReminderParentRow({ txn, splitItems: modelSplitItems, scheduledTxnsById })) {
                bucketizeTxnItem(
                  mkTransactionItem({
                    transaction: txn,
                    item: parentObj,
                  }),
                );
              }

              // Emit transfer split lines for paired display in Transfers section.
              modelSplitItems.forEach((splitItem, index) => {
                if (!transactionsUtils.isTransferOrCreditCardPaymentTxn(splitItem)) return;

                const splitTransferId = splitItem.transfer?.id ?? null;
                const otherSideForSplit = splitTransferId ? transactions.get(splitTransferId) : undefined;
                const effectiveSplitItemObj = getTransferSplitItemForF2S(splitItem, otherSideForSplit);
                if (!effectiveSplitItemObj) return;

                const splitItemIsExcludedFromF2S =
                  splitItem.isExcludedFromF2S ?? otherSideForSplit?.isExcludedFromF2S ?? txn.isExcludedFromF2S;
                const stableSplitId = transactionsUtils.getStableSplitItemId(txn, effectiveSplitItemObj, index);
                // Preserve the split row but zero business amounts for Spending Plan math.
                const displaySplitItem = getSplitItemWithBusinessZeroed(effectiveSplitItemObj);
                const item = {
                  ...txn.toObject(),
                  ...displaySplitItem,
                  // Ensure transfer linkage is per-split-line (prevents non-transfer lines from being treated as transfers).
                  transfer: effectiveSplitItemObj.transfer ?? null,
                  isExcludedFromF2S: splitItemIsExcludedFromF2S,
                  id: stableSplitId,
                };

                bucketizeTxnItem(
                  mkTransactionItem({
                    transaction: txn,
                    splitItem: mkSplitItem({
                      ...(displaySplitItem as Partial<SplitItemProps>),
                      isExcludedFromF2S: splitItemIsExcludedFromF2S,
                    }),
                    item,
                  }),
                );

                if (otherSideForSplit) {
                  bucketizeOtherSideTransferRow(txn, effectiveSplitItemObj, otherSideForSplit);
                }
              });
              return;
            }
            // Post-match: skip parent, fall through to emit model split lines.
          }

          modelSplitItems.forEach((splitItem, index) => {
            const splitTransferId = splitItem.transfer?.id ?? null;
            const otherSideForSplit = splitTransferId ? transactions.get(splitTransferId) : undefined;
            const effectiveSplitItemObj = getTransferSplitItemForF2S(splitItem, otherSideForSplit);
            if (!effectiveSplitItemObj) return;
            // Only exclude business split lines when they would be displayed individually.
            if (isBusinessSplitItem(effectiveSplitItemObj)) return;
            const splitItemIsExcludedFromF2S = getReminderSplitLineIsExcludedFromF2S({
              txn,
              splitItem: effectiveSplitItemObj,
              rawSplitItem: splitItem,
              scheduledTxnsById,
              otherSideForSplit,
            });
            const stableSplitId = transactionsUtils.getStableSplitItemId(txn, effectiveSplitItemObj, index);
            // Preserve the split row but zero business amounts for Spending Plan math.
            const displaySplitItem = getSplitItemWithBusinessZeroed(effectiveSplitItemObj);
            const item = {
              ...txn.toObject(),
              ...displaySplitItem,
              // Ensure transfer linkage is per-split-line (prevents non-transfer lines from being treated as transfers).
              transfer: effectiveSplitItemObj.transfer ?? null,
              isExcludedFromF2S: splitItemIsExcludedFromF2S,
              id: stableSplitId,
            };

            bucketizeTxnItem(
              mkTransactionItem({
                transaction: txn,
                splitItem: mkSplitItem({
                  ...(displaySplitItem as Partial<SplitItemProps>),
                  isExcludedFromF2S: splitItemIsExcludedFromF2S,
                }),
                item,
              }),
            );

            if (otherSideForSplit && transactionsUtils.isTransferOrCreditCardPaymentTxn(effectiveSplitItemObj)) {
              bucketizeOtherSideTransferRow(txn, effectiveSplitItemObj, otherSideForSplit);
            }
          });
          return;
        }

        const item = txn.toObject();
        if (item?.id) {
          bucketizeTxnItem(
            mkTransactionItem({
              transaction: txn,
              item,
            }),
          );
        }
        return;
      }

      // Spending Plan: reminders with splits should show:
      // - parent reminder when NOT matched/linked
      // - parent reminder + child split lines when matched/linked (children kept in canonical array order)
      // For non-pending transactions with splits we show split lines (no parent).
      // When both splits are business accounts, it should be excluded just like regular business txns
      // When one of the splits is a bussiness category, exclude that split specifically
      const isReminder = txn.source === 'SCHEDULED_TRANSACTION_PENDING';
      const isReminderMatchedOrLinked = transactionsUtils.isTxnMatchedOrLinked(txn);

      const instanceSplitItems = splitItemsToArrayOverflowLast(txn.split?.items ?? []);
      const modelSplitItems = isReminder ? getSTModelSplitItems(txn) : [];
      const orderedSplitItems = getOrderedSplitItemsForF2S(modelSplitItems, instanceSplitItems);
      // Ordered parallel to orderedSplitItems by index so series-model exclusion flags can fall through
      // when the instance overlay row lacks them. orderedSplitItems indices < orderedModelSplitItems.length
      // line up with the model side; instance-only extras past that point have no model partner.
      const orderedModelSplitItems = isReminder ? splitItemsToArrayOverflowLast(modelSplitItems) : [];
      const keepMatchedOverflowInBills =
        getReminderSeriesBucket({
          txn,
          scheduledTxnsById,
          incomeCOAIds: incomeCoas,
        }) !== null;

      const splitSourceItems = isReminder && modelSplitItems.length > 0 ? modelSplitItems : orderedSplitItems;
      // Entirely business splits should not appear in Spending Plan at all.
      if (isBusinessOnlySplit(splitSourceItems)) return;
      // Subtract business split amounts from parent rows to keep totals free of business values.
      const businessSplitAmount = getBusinessSplitAmountTotal(splitSourceItems);
      const splitItemsForAmounts = getSplitItemsWithBusinessZeroed(splitSourceItems);
      const adjustedParentFallbackAmount =
        businessSplitAmount !== 0 ? Number(txn.amount ?? 0) - businessSplitAmount : Number(txn.amount ?? 0);
      const reminderPrimaryCoa = isReminder ? getReminderParentCoa(splitSourceItems) : null;

      const nonTransferInstanceAmount = isReminder
        ? getReminderIncludedNonTransferAmount({
            txn,
            splitItems: splitItemsForAmounts,
            rawSplitItems: splitSourceItems,
            scheduledTxnsById,
          })
        : undefined;
      // Exclusion is resolved against the instance-preferred ordered splits so that per-instance
      // overrides (user excluded a split on this reminder only) are honored — matches the per-split
      // renderer below which also walks orderedSplitItems. orderedModelSplitItems is passed in parallel
      // so series-level exclusions on the model still apply when the instance overlay didn't copy them.
      const reminderExclusionSummary = isReminder
        ? getReminderSplitExclusionSummary({
            txn,
            splitItems: orderedSplitItems,
            rawSplitItems: orderedSplitItems,
            modelSplitItems: orderedModelSplitItems,
            scheduledTxnsById,
          })
        : null;
      const isIncomeReminderParent =
        reminderPrimaryCoa?.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY &&
        incomeCoas.has(reminderPrimaryCoa.id ?? '');
      const reminderParentAmount = reminderExclusionSummary?.allRelevantNonTransferLinesExcluded
        ? reminderExclusionSummary.excludedRelevantAmount
        : isIncomeReminderParent
          ? getIncomeReminderParentAmount({
              splitItems: splitItemsForAmounts,
              plannedCoaIds,
              fallbackAmount: adjustedParentFallbackAmount,
            })
          : // For bills, fall back to the non-transfer amount minus business splits.
            (nonTransferInstanceAmount ?? (businessSplitAmount !== 0 ? adjustedParentFallbackAmount : undefined));
      const baseParentObj = txn.toObject();
      const reminderParentExclusionOverride = getReminderParentExclusionOverride(reminderExclusionSummary);
      const reminderParentObj = {
        ...baseParentObj,
        ...(reminderPrimaryCoa ? { coa: reminderPrimaryCoa } : undefined),
        ...(reminderParentAmount !== undefined ? { amount: reminderParentAmount } : undefined),
        // Avoid leaking a parent-level transfer link onto non-transfer rollup rows.
        ...(reminderParentAmount !== undefined ? { transfer: null } : undefined),
        ...(reminderParentExclusionOverride != null
          ? { isExcludedFromF2S: reminderParentExclusionOverride }
          : undefined),
      };

      if (isReminder) {
        if (!isReminderMatchedOrLinked) {
          // Pre-match: emit parent row.
          if (txn.id && shouldEmitReminderParentRow({ txn, splitItems: orderedSplitItems, scheduledTxnsById })) {
            bucketizeTxnItem(
              mkTransactionItem({
                transaction: txn,
                item: reminderParentObj,
              }),
            );
          }

          // Emit transfer split lines for paired display in Transfers section.
          orderedSplitItems.forEach((splitItem, index) => {
            if (!transactionsUtils.isTransferOrCreditCardPaymentTxn(splitItem)) return;

            const splitTransferId = splitItem.transfer?.id ?? null;
            const otherSideForSplit = splitTransferId ? transactions.get(splitTransferId) : undefined;
            const effectiveSplitItemObj = getTransferSplitItemForF2S(splitItem, otherSideForSplit);
            if (!effectiveSplitItemObj) return;

            const splitItemIsExcludedFromF2S =
              splitItem.isExcludedFromF2S ?? otherSideForSplit?.isExcludedFromF2S ?? txn.isExcludedFromF2S;
            const stableSplitId = transactionsUtils.getStableSplitItemId(txn, effectiveSplitItemObj, index);
            // Preserve the split row but zero business amounts for Spending Plan math.
            const displaySplitItem = getSplitItemWithBusinessZeroed(effectiveSplitItemObj);
            const item = {
              ...txn.toObject(),
              ...displaySplitItem,
              // Ensure transfer linkage is per-split-line (prevents non-transfer lines from being treated as transfers).
              transfer: effectiveSplitItemObj.transfer ?? null,
              isExcludedFromF2S: splitItemIsExcludedFromF2S,
              id: stableSplitId,
            };

            bucketizeTxnItem(
              mkTransactionItem({
                transaction: txn,
                splitItem: mkSplitItem({
                  ...(displaySplitItem as Partial<SplitItemProps>),
                  isExcludedFromF2S: splitItemIsExcludedFromF2S,
                }),
                item,
              }),
            );

            if (otherSideForSplit) {
              bucketizeOtherSideTransferRow(txn, effectiveSplitItemObj, otherSideForSplit);
            }
          });
          return;
        }
        // Post-match: skip parent, fall through to emit split lines.
      }

      orderedSplitItems.forEach((splitItem, index) => {
        if (isZeroAmountOverflowSplit(splitItem)) return;
        const includeOverflowInBills = keepMatchedOverflowInBills && isOverflowSplitItem(splitItem);
        const splitTransferId = splitItem.transfer?.id ?? null;
        const otherSideForSplit = splitTransferId ? transactions.get(splitTransferId) : undefined;
        const effectiveSplitItemObj = includeOverflowInBills
          ? splitItem
          : getTransferSplitItemForF2S(splitItem, otherSideForSplit);
        if (!effectiveSplitItemObj) return;
        // Only exclude business split lines when they would be displayed individually.
        if (isBusinessSplitItem(effectiveSplitItemObj)) return;
        const resolvedSplitExclusion = getReminderSplitLineIsExcludedFromF2S({
          txn,
          splitItem: effectiveSplitItemObj,
          rawSplitItem: splitItem,
          modelSplitItem: orderedModelSplitItems[index],
          scheduledTxnsById,
          otherSideForSplit,
        });
        // The matched overflow row inherits its exclusion from the rest of the reminder: if every
        // relevant real split is excluded, the overflow follows them into Excluded too.
        const splitItemIsExcludedFromF2S =
          isOverflowSplitItem(splitItem) && reminderExclusionSummary?.allRelevantNonTransferLinesExcluded
            ? true
            : resolvedSplitExclusion;
        const stableSplitId = transactionsUtils.getStableSplitItemId(txn, effectiveSplitItemObj, index);
        // Preserve the split row but zero business amounts for Spending Plan math.
        const displaySplitItem = getSplitItemWithBusinessZeroed(effectiveSplitItemObj);
        const item = {
          ...txn.toObject(),
          ...displaySplitItem,
          // Ensure transfer linkage is per-split-line (prevents non-transfer lines from being treated as transfers).
          transfer: effectiveSplitItemObj.transfer ?? null,
          isExcludedFromF2S: splitItemIsExcludedFromF2S,
          id: stableSplitId,
        };

        bucketizeTxnItem(
          mkTransactionItem({
            transaction: txn,
            splitItem: mkSplitItem({
              ...(displaySplitItem as Partial<SplitItemProps>),
              isExcludedFromF2S: splitItemIsExcludedFromF2S,
            }),
            item,
          }),
        );

        // If this split line is a transfer, ensure the other side is present in the Bills transfer list
        // so it can render as a paired row (even if it would otherwise be suppressed as a matched txn).
        if (otherSideForSplit) {
          bucketizeOtherSideTransferRow(txn, effectiveSplitItemObj, otherSideForSplit);
        }
      });
    });

    const txnsIncomeIncluded = ImmutableList(txnsIncomeIncludedArray);
    const txnsIncomeExcluded = ImmutableList(txnsIncomeExcludedArray);
    const txnsBillsIncluded = ImmutableList(txnsBillsIncludedArray);
    const txnsBillsExcluded = ImmutableList(txnsBillsExcludedArray);

    return {
      income: {
        includeData: txnsIncomeIncluded,
        excludeData: txnsIncomeExcluded,
        excludedAmount: getItemsSum(txnsIncomeExcludedArray),
        includedAmount:
          !earliestDate || earliestDate < freeToSpend.date
            ? getIncludedItemsSum(txnsIncomeIncludedArray)
            : freeToSpend.calculatedIncomeAmount || 0,
        overwrittenAmount: isNumber(freeToSpend.overwrittenIncomeAmount)
          ? freeToSpend.overwrittenIncomeAmount
          : undefined,
      },
      bills: {
        includeData: txnsBillsIncluded,
        excludeData: txnsBillsExcluded,
        excludedAmount: getItemsSum(txnsBillsExcludedArray),
        includedAmount:
          !earliestDate || earliestDate < freeToSpend.date
            ? getIncludedItemsSum(txnsBillsIncludedArray)
            : freeToSpend.calculatedBillsAmount || 0,
        overwrittenAmount: isNumber(freeToSpend.overwrittenBillsAmount)
          ? freeToSpend.overwrittenBillsAmount
          : undefined,
      },
    };
  },
);

// Amount reader: same inputs and calculation cache as the detail selector, fed memo-insensitive rows.
const getSpendingPlanAmountBreakdown = createSelector(
  breakdownInputs(getSummaryTransactionsForMonth),
  getSpendingPlanBreakdownDataWithSplitsSelectorV2.memoizedResultFunc,
);

// returns txnItems instead of txnRecords in breakdownLists
export const getSpendingPlanBreakdownDataWithSplitsV2 = (
  state: State,
  freeToSpend: FreeToSpend,
): ReturnType<typeof getSpendingPlanBreakdownDataWithSplitsSelectorV2> =>
  getSpendingPlanBreakdownWithTracking(state, freeToSpend, getSpendingPlanBreakdownDataWithSplitsSelectorV2);

type SpendingPlanAmounts = Pick<
  ReturnType<typeof getSpendingPlanBreakdownDataWithSplitsSelectorV2>['income'],
  'includedAmount' | 'excludedAmount' | 'overwrittenAmount'
>;
type IncomeAndBillsAmounts = { income: SpendingPlanAmounts; bills: SpendingPlanAmounts };

/** Amount-only boundary: transaction detail rows must use the full breakdown selector. */
export const getSpendingPlanIncomeAndBillsAmounts = createSelector(
  [
    (state: State, freeToSpend: FreeToSpend) =>
      getSpendingPlanBreakdownWithTracking(state, freeToSpend, getSpendingPlanAmountBreakdown),
  ],
  ({ income, bills }): IncomeAndBillsAmounts => ({
    income: {
      includedAmount: income.includedAmount,
      excludedAmount: income.excludedAmount,
      overwrittenAmount: income.overwrittenAmount,
    },
    bills: {
      includedAmount: bills.includedAmount,
      excludedAmount: bills.excludedAmount,
      overwrittenAmount: bills.overwrittenAmount,
    },
  }),
);

export const getSpendingPlanIncomeForDisplayV2 = createSelector([getSpendingPlanBreakdownDataWithSplitsV2], (data) => {
  const incomeData = data.income;
  const income: SpendingPlanBreakDownForPageDisplay = {
    includedAmount: incomeData.includedAmount,
    excludedAmount: incomeData.excludedAmount,
    overwrittenAmount: incomeData.overwrittenAmount,
    includeExpansionPanel: getNormalizedSortedTxns(incomeData.includeData),
    excludeExpansionPanel: getNormalizedSortedTxns(incomeData.excludeData),
  };
  return income;
});

export const getSpendingPlanBillsForDisplayV2 = createSelector(
  [
    getSpendingPlanBreakdownDataWithSplitsV2,
    (state) => preferencesV2Selectors.getPreferenceValue(state, 'spendingPlanBillGrouping'),
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'spendingPlanBillGrouping'),
    (_state: State, _freeToSpend: FreeToSpend, filter?: BillsFilterType) => filter,
  ],
  (data, shouldSort, billSortFF, filter) => {
    const billsData = data.bills;
    const shouldSortByBillType = shouldSort && billSortFF;
    const bills: SpendingPlanBreakDownForPageDisplay = {
      includedAmount: billsData.includedAmount,
      excludedAmount: billsData.excludedAmount,
      overwrittenAmount: billsData.overwrittenAmount,
      includeExpansionPanel: getNormalizedSortedTxns(billsData.includeData, shouldSortByBillType, filter),
      excludeExpansionPanel: getNormalizedSortedTxns(billsData.excludeData),
    };
    return bills;
  },
);

export const getSpendingPlanGoalsForDisplayV2 = createSelector([getSpendingPlanGoalsBreakdownData], (goalsData) => {
  const goals: SpendingPlanBreakDownForPageDisplay = {
    includedAmount: goalsData.includedAmount,
    excludedAmount: goalsData.excludedAmount,
    overwrittenAmount: goalsData?.overwrittenAmount,
    includeExpansionPanel: getNormalizedSortedGoals(goalsData.includeData),
    excludeExpansionPanel: getNormalizedSortedGoals(goalsData.excludeData),
  };
  return goals;
});

// ======================================================== Left To Spend Calculations ==================================================================

export const getIncomeAfterBillsAndSavings = (state: State, freeToSpend: FreeToSpend) => {
  const spendingPlanClientCalc = featureFlagsSelectors.getFeatureFlag(state, 'spendingPlanClientCalc');
  if (!spendingPlanClientCalc) {
    return freeToSpend.totalToSpendAmount;
  }
  return getIncomeAfterBillsAndSavingsSelector(state, freeToSpend);
};

export const getIncomeAfterBillsAndSavingsSelector = createSelector(
  [
    (_state, freeToSpend: FreeToSpend) => freeToSpend,
    getSpendingPlanIncomeAndBillsAmounts,
    transactionsSelectors.getDateOnAfter,
    getSpendingPlanGoalsBreakdownData,
    getBreakdownLoadPending,
  ],
  (freeToSpend, breakdownData, earliestDate, goalsData, loadPending) => {
    if (loadPending || (earliestDate && earliestDate > freeToSpend.date)) {
      return freeToSpend.totalToSpendAmount ?? 0;
    }

    let incomeAmount = breakdownData.income.includedAmount;
    let billsAmount = breakdownData.bills.includedAmount;
    let { includedAmount: goalsAmount } = goalsData;

    // Check overwrites and use them.
    if (isNumber(freeToSpend.overwrittenIncomeAmount)) {
      incomeAmount = freeToSpend.overwrittenIncomeAmount ?? 0;
    }
    if (isNumber(freeToSpend.overwrittenBillsAmount)) {
      billsAmount = freeToSpend.overwrittenBillsAmount ?? 0;
    }
    if (isNumber(freeToSpend.overwrittenGoalsAmount)) {
      goalsAmount = freeToSpend.overwrittenGoalsAmount ?? 0;
    }
    return roundOff(incomeAmount + billsAmount + goalsAmount, 2);
  },
);

export const getSpentAmount = (state: State, freeToSpend: FreeToSpend, includeProjected?: boolean) => {
  const spendingPlanClientCalc = featureFlagsSelectors.getFeatureFlag(state, 'spendingPlanClientCalc');
  if (!spendingPlanClientCalc) {
    return freeToSpend.spentAmount;
  }
  if (includeProjected && isFreeToSpendFutureMonth(state, freeToSpend)) {
    return freeToSpend?.projectedOtherSpending ?? 0;
  }
  // below selector runs through txns so is expensive - keep this lean if possible
  return getSpentAmountSelector(state, freeToSpend) ?? freeToSpend?.spentAmount ?? 0;
};

export const getSpentAmountSelector = createSelector(
  [
    (state: State, freeToSpend: FreeToSpend) =>
      getOtherSpendingForAmounts(state, getSpendingPlanDateById(state, freeToSpend.id ?? '')),
    (state, freeToSpend: FreeToSpend) => getPlannedCoaIds(state, freeToSpend.date),
    categoriesSelectors.getIncomeCOAIds,
  ],
  (spent, plannedCoas, incomeCoas) => {
    const excludedCoas = ImmutableSet([...plannedCoas, ...incomeCoas].filter((id): id is string => !!id));
    const chartSum =
      getTxnSumWithCallback?.(
        spent.chartTxns,
        (item: TransactionRecord) => transferAndCoaAndBizCallback(item, excludedCoas),
        false,
      ) ?? 0;
    return chartSum + (spent.billOverflowAmount ?? 0);
  },
);

let leftToSpendCalcNumber = 0;
export const getLeftToSpendSelector = createSelector(
  [getIncomeAfterBillsAndSavings, getPlannedSpendingAmount, getSpentAmount],
  (incomeAfterBillsAndSavings, plannedSpendingAmount, spentAmount) => {
    leftToSpendCalcNumber += 1;
    return numberUtils.round((incomeAfterBillsAndSavings ?? 0) + (plannedSpendingAmount ?? 0) + (spentAmount ?? 0));
  },
);

export const getLeftToSpend = (state: State, freeToSpend: FreeToSpend, includeProjected?: boolean) => {
  const spendingPlanClientCalc = featureFlagsSelectors.getFeatureFlag(state, 'spendingPlanClientCalc');
  if (!spendingPlanClientCalc) {
    const isFutureMonth = isFreeToSpendFutureMonth(state, freeToSpend);
    return includeProjected && isFutureMonth
      ? (freeToSpend?.projectedOtherSpending ?? 0)
      : freeToSpend.leftToSpendAmount;
  }
  const startTime = performance.now();
  const calcNumber = leftToSpendCalcNumber + 1;
  const clientCalc = getLeftToSpendSelector(state, freeToSpend, includeProjected);
  if (calcNumber === leftToSpendCalcNumber) {
    // only report time if we have actually hit the combiner for recalc
    tracker.track(tracker.events.spendingPlanClientCalc, {
      calcTime: performance.now() - startTime,
      type: 'leftToSpend',
    });
  }
  return clientCalc;
};

// ======================================================== END Left To Spend Calculations ==================================================================

// ==================================================================== OTHER SPENDING NODE SELECTORS ====================================================================
export type COANodeProps = {
  id: string;
  parentId: string;
  coas: ImmutableSet<chartOfAccountsTypes.ChartOfAccountProps>;
  transactions: ImmutableMap<string, TransactionRecord>;
  balance: number;
  label?: string | undefined;
  logoUrl?: string | undefined;
};
export type COANode = RecordOf<COANodeProps>;
type NodeTransactionInput = Pick<TransactionProps, 'id' | 'split' | 'coa' | 'amount' | 'postedOn'> | TransactionRecord;
const mkCOANode = Record<COANodeProps>({
  id: '0',
  parentId: '0',
  coas: ImmutableSet<chartOfAccountsTypes.ChartOfAccountProps>(),
  transactions: ImmutableMap<string, TransactionRecord>(),
  balance: 0,
  label: undefined,
  logoUrl: undefined,
});
export type NodeMap = OrderedMap<string, COANodeProps>;
const emptyNodes = OrderedMap<string, COANodeProps>();

const updateNodeWithTransaction = (
  coaNodesTree: OrderedMap<string, COANode>,
  id: string,
  parentId: string,
  label: string | undefined,
  logoUrl: string | undefined,
  transaction: NodeTransactionInput,
  amount: number,
  coa?: chartOfAccountsTypes.ChartOfAccountProps,
) => {
  const normalizedTransaction = mkCashFlowTransaction(transaction);
  const transactionId = normalizedTransaction.id;

  if (!assert(id, 'fts node id should be defined') || !transactionId) {
    return;
  }
  const coaNode =
    coaNodesTree.get(id) ||
    new mkCOANode({
      id,
      parentId,
      label,
      logoUrl,
    });
  const updatedNode = coaNode.merge({
    transactions: coaNode.transactions.set(transactionId, normalizedTransaction),
    balance: coaNode.balance + Number(amount),
    coas: coa ? coaNode.coas.add(coa) : coaNode.coas,
    logoUrl,
  });
  coaNodesTree.set(id, updatedNode);
};

// Drops a leaf that is its parent's only child, which repeats the parent. Comparing their totals would also drop a
// leaf whose siblings in a split net to zero.
const dedupeOrphanedLeaves = (coaNodesRow: NodeMap): NodeMap => {
  const childCounts = coaNodesRow.countBy((node) => node.parentId);
  return coaNodesRow.filter((node) => {
    const parent = coaNodesRow.get(node.parentId);
    return !parent || parent.id === '0' || childCounts.has(node.id) || childCounts.get(parent.id) !== 1;
  });
};

const compactNodeTree = (coaNodesRow: NodeMap, maxNodesNum: number): NodeMap => {
  const coaNodesTrimmed = OrderedMap<string, COANodeProps>().withMutations((coaNodes) => {
    const coaNodesByParentId = new Map<string, COANodeProps[]>();
    coaNodesRow.forEach((node) => {
      const existingNodes = coaNodesByParentId.get(node.parentId);
      if (existingNodes) {
        existingNodes.push(node);
      } else {
        coaNodesByParentId.set(node.parentId, [node]);
      }
    });

    coaNodesByParentId.forEach((coaNodesGroup, parentId) => {
      const coaNodesGroupSorted = [...coaNodesGroup].sort((node1, node2) =>
        node1.balance !== node2.balance
          ? node2.balance - node1.balance
          : (node1.label ?? '').localeCompare(node2.label ?? ''),
      );
      if (coaNodesGroupSorted.length > maxNodesNum) {
        const nodesToKeep = coaNodesGroupSorted.slice(-(maxNodesNum - 1));
        const otherId = `${parentId}-others`;
        const otherNodes = coaNodesGroupSorted.slice(0, -(maxNodesNum - 1)).map(
          (node) =>
            new mkCOANode({
              id: node.id,
              parentId: otherId,
              coas: node.coas,
              transactions: node.transactions,
              balance: node.balance,
              label: node.label,
              logoUrl: node.logoUrl,
            }),
        );
        const otherBalance = otherNodes.reduce((balance, node) => balance + node.balance, 0);
        const otherTransactions = otherNodes.reduce(
          (transactions, node) => transactions.merge(node.transactions),
          ImmutableMap<string, TransactionRecord>(),
        );
        const otherCOAs = otherNodes.reduce(
          (coas, node) => coas.merge(node.coas),
          ImmutableSet<chartOfAccountsTypes.ChartOfAccountProps>(),
        );
        const otherNode = new mkCOANode({
          id: otherId,
          parentId,
          transactions: otherTransactions,
          balance: otherBalance,
          label: 'Other',
          coas: otherCOAs,
        });
        coaNodes.set(otherId, otherNode);
        nodesToKeep.forEach((node) => {
          coaNodes.set(node.id, node);
        });
        otherNodes.forEach((node) => {
          coaNodes.set(node.id, node);
        });
      } else {
        coaNodesGroupSorted.forEach((node) => {
          coaNodes.set(node.id, node);
        });
      }
    });
  });
  return coaNodesTrimmed;
};

export const isFreeToSpendFutureMonth = createSelector(
  [dateSelectors.getCurrentDate, (state: State, freeToSpend: FreeToSpend) => freeToSpend.date],
  (currentDate, freeToSpendDate) => {
    const currentDateString = currentDate.startOf('month').toISODate() as string;
    return currentDateString < freeToSpendDate;
  },
);

export const getNodesFromTransactions = createSelector(
  [
    categoriesSelectors.getCategoriesById,
    accountsSelectors.getAccountsById,
    selectorsHelpers.createTrimSelector(
      (state: State, transactions?: ImmutableMap<string, TransactionRecord>) =>
        transactions || transactionsSelectors.getTransactionsById(state),
      (transaction: TransactionRecord) => ({
        id: transaction.id,
        split: transaction.split,
        coa: transaction.coa,
        amount: transaction.amount,
        postedOn: transaction.postedOn,
      }),
    ),
    (_state, _transactions, maxNodesNum: number) => maxNodesNum,
    selectorsHelpers.createValueCache(
      (state: State, _transactions: unknown, _maxNodesNum: unknown, date: string) =>
        getPlannedSpendingCoas(state, date, false) || null,
    ),
  ],
  (categoriesById, accountsById, transactionsById, maxNodesNum, plannedCoas) => {
    if (!transactionsById || transactionsById.size === 0) {
      return emptyNodes;
    }
    const coaNodesRow = OrderedMap<string, COANode>().withMutations((coaNodes) => {
      transactionsById.forEach((transaction) => {
        const isSplit = transactionsUtils.isSplitTxn(transaction);
        const items = (isSplit ? transaction?.split?.items : [transaction]) as {
          amount: number;
          coa: chartOfAccountsTypes.ChartOfAccountProps;
          businessId?: string;
        }[];
        items?.forEach((item) => {
          if (item.businessId) return;
          let id = chartOfAccountsUtils.getCoaId(item.coa);
          if (item.coa && item.coa.type === chartOfAccountsTypes.CoaTypeEnum.ACCOUNT) {
            const account = accountsById.get(id);
            const label = account?.name || 'Unknown';
            // add node for account-specific transfer
            updateNodeWithTransaction(
              coaNodes,
              `${id}-explicit`,
              id,
              label,
              undefined,
              transaction,
              item.amount,
              item.coa,
            );
            // update baseline 'transfer' bucket
            updateNodeWithTransaction(
              coaNodes,
              id,
              'BALANCE_ADJUSTMENT_2',
              label,
              undefined,
              transaction,
              item.amount,
              item.coa,
            );
            updateNodeWithTransaction(
              coaNodes,
              'BALANCE_ADJUSTMENT_2',
              '0',
              'Transfer',
              undefined,
              transaction,
              item.amount,
              item.coa,
            );
          } else if (item.coa && item.coa.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY) {
            let category = categoriesById.get(id);
            if (category?.type === categoriesTypes.CategoryTypeEnum.INCOME || plannedCoas?.has(id)) {
              return;
            }
            let label = (category && category.name) || 'Unknown';
            const logoUrl = category && category.logoUrl;
            const idExplicit = `${id}-explicit`;
            // add value to base COA
            updateNodeWithTransaction(coaNodes, idExplicit, id, label, logoUrl, transaction, item.amount, item.coa);

            // add value to parent COAs
            for (let loopProtect = 0; loopProtect < 255 && id !== '0'; loopProtect++) {
              category = categoriesById.get(id);
              const parentId = category?.parentId || '0';
              label = (category && category.name) || 'Unknown';
              updateNodeWithTransaction(coaNodes, id, parentId, label, logoUrl, transaction, item.amount, item.coa);
              id = parentId;
            }
          } else if (!transactionsUtils.isTransferOrCreditCardPaymentTxn(item)) {
            const label = chartOfAccountsUtils.getCoaString(item.coa, categoriesById, accountsById, true);
            updateNodeWithTransaction(coaNodes, id, '0', label, undefined, transaction, item.amount, item.coa);
          }
          updateNodeWithTransaction(coaNodes, '0', '', 'All', undefined, transaction, item.amount, item.coa); // update root
        });
      });
    });

    return compactNodeTree(dedupeOrphanedLeaves(coaNodesRow), maxNodesNum);
  },
);

const getOtherSpendingNodesSelector = createSelector(
  [
    categoriesSelectors.getCategoriesById,
    accountsSelectors.getAccountsById,
    (_state, transactionItems?: ImmutableList<TransactionItem>) => transactionItems ?? emptyItemsList,
    (_state, _transactionItems, maxNodesNum: number) => maxNodesNum,
    selectorsHelpers.createValueCache(
      (state: State, _transactionItems: unknown, _maxNodesNum: unknown, date: string) =>
        getPlannedSpendingCoas(state, date, false) || null,
    ),
  ],
  (categoriesById, accountsById, transactionItems, maxNodesNum, plannedCoas) => {
    if (!transactionItems || transactionItems.size === 0) {
      return emptyNodes;
    }

    const coaNodesRow = OrderedMap<string, COANode>().withMutations((coaNodes) => {
      transactionItems.forEach((transactionItem) => {
        const transaction = transactionItem.transaction;
        const item = transactionItem.item;
        const transferCandidate = transactionItem.splitItem ?? transaction;

        if (!transaction || !item || item.isExcludedFromF2S) return;
        if (item.businessId || transactionsUtils.isTransferOrCreditCardPaymentTxn(transferCandidate)) return;

        const amount = Number(item.amount ?? 0);
        let id = chartOfAccountsUtils.getCoaId(item.coa);

        if (item.coa && item.coa.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY) {
          let category = categoriesById.get(id);
          if (category?.type === categoriesTypes.CategoryTypeEnum.INCOME || plannedCoas?.has(id)) {
            return;
          }
          let label = (category && category.name) || 'Unknown';
          const logoUrl = category && category.logoUrl;
          const idExplicit = `${id}-explicit`;

          updateNodeWithTransaction(coaNodes, idExplicit, id, label, logoUrl, transaction, amount, item.coa);

          for (let loopProtect = 0; loopProtect < 255 && id !== '0'; loopProtect++) {
            category = categoriesById.get(id);
            const parentId = category?.parentId || '0';
            label = (category && category.name) || 'Unknown';
            updateNodeWithTransaction(coaNodes, id, parentId, label, logoUrl, transaction, amount, item.coa);
            id = parentId;
          }
        } else {
          const label = chartOfAccountsUtils.getCoaString(item.coa, categoriesById, accountsById, true);
          updateNodeWithTransaction(coaNodes, id, '0', label, undefined, transaction, amount, item.coa ?? undefined);
        }

        updateNodeWithTransaction(coaNodes, '0', '', 'All', undefined, transaction, amount, item.coa ?? undefined);
      });
    });

    return compactNodeTree(dedupeOrphanedLeaves(coaNodesRow), maxNodesNum);
  },
);

export const getOtherSpendingNodes = (state: State, id: string, maxNodesNum: number): NodeMap => {
  const freeToSpend = getFreeToSpendById(state, id);
  const date = freeToSpend?.date ?? (dateSelectors.getCurrentDate(state).startOf('month').toISODate() as string);
  const { listTxns } = getOtherSpendingTxnsWithSplitsSelector(state, date);
  return getOtherSpendingNodesSelector(state, listTxns, maxNodesNum, date);
};

export const childNodes = createCachedSelector(
  (allNodes: NodeMap) => allNodes,
  (_allNodes: unknown, nodeId: string) => nodeId,
  (allNodes, nodeId) => allNodes && allNodes.filter((node: COANodeProps) => node.parentId === nodeId),
)({
  keySelector: (allNodes: NodeMap, nodeId: string) => `${allNodes?.hashCode()}:${nodeId}`,
  cacheObject: new LruObjectCache({ cacheSize: 22 }), // need larger cache as this is called for each node in the graph
});

export const cachedNodesFromNode = createCachedSelector(
  (node?: COANodeProps) => node,
  (node?: COANodeProps) => (node?.id ? OrderedMap<string, COANodeProps>([[node.id, node]]) : undefined),
)({
  keySelector: (node) => `${node?.id}`,
  cacheObject: new LruObjectCache({ cacheSize: 4 }),
});

export const coasForNodes = createSelectorBase(
  (nodes?: NodeMap) => nodes,
  (nodes) =>
    ImmutableSet<chartOfAccountsTypes.ChartOfAccountProps>().withMutations(
      (coas) => nodes && nodes.reduce((coasAccumulator, node) => coasAccumulator.merge(node.coas), coas),
    ),
);

export const mergeCollections = createCachedSelector(
  (collectionArray: RecordOf<any>[]) => collectionArray,
  (collectionArray) => {
    const collectionArrayNormalized = collectionArray.filter((collection) => collection);
    return collectionArrayNormalized && collectionArrayNormalized.length
      ? merge(collectionArrayNormalized[0], ...collectionArrayNormalized.slice(1))
      : undefined;
  },
)({
  keySelector: (collectionArray) => collectionArray?.map((collection) => collection?.hashCode?.()).join(':') ?? 'none',
  cacheObject: new LruObjectCache({ cacheSize: 10 }),
  selectorCreator: selectorsHelpers.createArraysEqualShallowSelector,
});

// note - the reason we are using getSortedTransactionsByIds is due to the fact that this is the same selector used in planBreakdown, so the cache is already saved and
// we don't have to do anymore expensive calculations
export const closeOutPriorMonth = createSelector(
  [
    // TODO: this still uses QCS txnIDs for lookup - ideally move to client-side evaluation (like re-using breakdownData)
    (state, freeToSpend: FreeToSpend) =>
      transactionsSelectors.getSortedTransactionsByIds(state, freeToSpend?.incomeTxnIds),
    (state, freeToSpend: FreeToSpend) =>
      transactionsSelectors.getSortedTransactionsByIds(state, freeToSpend?.billsTxnIds),
    (state, freeToSpend: FreeToSpend) =>
      transactionsSelectors.getSortedTransactionsByIds(state, freeToSpend?.subscriptionsTxnIds),
    (state, freeToSpend: FreeToSpend) =>
      transactionsSelectors.getSortedTransactionsByIds(state, freeToSpend?.spentTxnIds),
    (state, freeToSpend: FreeToSpend) =>
      transactionsSelectors.getSortedTransactionsByIds(state, freeToSpend?.transferTxnIds),
    (state, freeToSpend: FreeToSpend) => getCalculatedPlannedSpendingObject(state, freeToSpend.date),
    (_state, freeToSpend: FreeToSpend) => freeToSpend,
  ],
  (incomeTxns, billTxns, subTxns, spentTxns, transferTxns, calculatedSpendingInfo, freeToSpend) => {
    let hasPendingTxns = false;
    const filterTxns = (txn: TransactionRecord) => {
      if (
        !hasPendingTxns &&
        txn.state === 'PENDING' &&
        txn.source !== 'SCHEDULED_TRANSACTION_PENDING' &&
        !txn.stModelId
      ) {
        hasPendingTxns = true;
      }
      return txn.source === 'SCHEDULED_TRANSACTION_PENDING';
    };
    const incomeLength = incomeTxns?.filter(filterTxns)?.size || 0;
    const billsLength = billTxns?.filter(filterTxns)?.size || 0;
    const subsLength = subTxns?.filter(filterTxns)?.size || 0;
    const transfersLength = transferTxns?.filter(filterTxns)?.size || 0;
    // this is for setting hasPendingTxns to true if they exist in spent
    spentTxns?.forEach(filterTxns);
    const plannedZeroArr: PlannedSpendingItem[] = [];
    const plannedArr = freeToSpend?.plannedSpendingItems?.filter((item) => {
      const calculatedIncluded = calculatedSpendingInfo[item.id]?.calculatedIncluded;
      if (
        Math.abs(calculatedIncluded || 0) === Math.abs(item.overwrittenTargetAmount ?? item.targetAmount) &&
        !validRolloverStates.includes(item?.rolloverType)
      ) {
        plannedZeroArr.push(item);
      }
      return (
        Math.abs(calculatedIncluded || 0) <= Math.abs(item.overwrittenTargetAmount ?? item.targetAmount) &&
        !validRolloverStates.includes(item?.rolloverType)
      );
    });
    const incomeAfterWarning = billsLength !== 0 || incomeLength !== 0 || subsLength !== 0 || transfersLength !== 0;

    return {
      incomeLength,
      billsLength,
      subsLength,
      transfersLength,
      plannedArr,
      plannedZeroArr,
      freeToSpendId: freeToSpend?.id,
      hasPendingTxns,
      incomeAfterWarning,
    };
  },
);

export const getPastFreeToSpends = createSelector(
  getResourcesById,
  dateSelectors.getCurrentDateString,
  (resources, currentDate) =>
    resources.filter((f2s) => {
      const today = DateTime.fromISO(currentDate).startOf('month');
      return DateTime.fromISO(f2s.date) < today;
    }),
);

export const getFutureFreeToSpendsIds = createSelector(
  getResourcesById,
  (_state, props: { startDate: string }) => props.startDate,
  (resources: ImmutableMap<string, FreeToSpend>, startDate) =>
    resources
      .filter((f2s) => {
        const today = DateTime.fromISO(startDate).startOf('month');
        return DateTime.fromISO(f2s.date) >= today;
      })
      .toList()
      .toArray()
      .map((f2s) => f2s.id),
);

export const getPastFreeToSpendAmounts = createSelector(
  [getPastFreeToSpends, (_state, endDate?: string) => endDate],
  (freeToSpends, endDate) => {
    const retArr: any[] = [];
    freeToSpends.forEach((f2s) => {
      const F2Sdate = DateTime.fromISO(f2s.date as string) as DateTime;
      if (!endDate || F2Sdate <= DateTime.fromISO(endDate)) {
        retArr.push({
          date: f2s.date,
          total: (f2s.spentAmount || 0) * -1,
          txns: undefined,
          category: F2Sdate.toFormat('LLL'),
          custom: F2Sdate.toFormat('LLL yyyy'),
        });
      }
      return true;
    });
    return retArr.sort((a, b) => (DateTime.fromISO(a.date) < DateTime.fromISO(b.date) ? -1 : 1));
  },
);

export const getAverageSpentFromF2S = createSelector(
  [
    (state, _months: number, endDate: string) => getPastFreeToSpendAmounts(state, endDate),
    (_state, months: number) => months,
  ],
  (pastFreeToSpends, months) => {
    if (pastFreeToSpends?.length > 0) {
      let max = Number.NEGATIVE_INFINITY;
      let min = 0;
      const sum = pastFreeToSpends.slice(pastFreeToSpends.length - months).reduce((accumulator, value: any) => {
        if (max < value.total) {
          max = value.total;
        }
        if (min > value.total) {
          min = value.total;
        }
        return accumulator + value.total;
      }, 0);
      return {
        average: sum / months,
        max,
        min,
        sum,
      };
    }
    return { average: 0, max: 0, sum: 0 };
  },
);

export const getProjectedOtherSpending = (freeToSpend: FreeToSpend) => freeToSpend.projectedOtherSpending || 0;

// ========== SPENDING PLAN SELECTORS END HERE =============

// A store reset drops every key's selector (see selectorCacheRegistry).
registerCachedSelectors(
  getTransactionCandidatesForMonth,
  getSummaryTransactionCandidatesForMonth,
  childNodes,
  cachedNodesFromNode,
  mergeCollections,
);
